// src/pages/Finanzas.js

import React, { useState, useEffect, useMemo } from 'react';
import { FaDollarSign, FaChartBar, FaArrowUp, FaArrowDown, FaCalculator, FaDownload, FaTimes, FaCheckCircle, FaExclamationTriangle, FaFileInvoiceDollar, FaWallet, FaHandshake, FaCreditCard, FaReceipt, FaBookmark, FaCoins, FaCalendarAlt, FaCalendarCheck, FaBolt, FaHistory, FaFileAlt, FaSpinner } from 'react-icons/fa';
import { db, functions, storage } from '../firebase';
import { collection, query, onSnapshot, orderBy, doc, updateDoc, serverTimestamp, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import ConfirmationModal from '../components/ConfirmationModal';
import ReportesModal from '../components/ReportesModal';
import { calcularAdeudoConInteres, esCasaCaseta } from '../utils/morosoUtils';
import { esPartidaContableResidente } from '../utils/conceptosContables';
import './Finanzas.css';

const Finanzas = () => {

    // --- ESTADOS ---
    const [casas, setCasas] = useState([]);
    const [ingresos, setIngresos] = useState([]);
    const [egresos, setEgresos] = useState([]);
    const [summary, setSummary] = useState({
        ingresosMes: 0,
        gastosMes: 0,
        balance: 0,
        porCobrar: 0,
        saldoFavorTotal: 0,
        netPorCobrar: 0,
        bolsaCentavos: 0,
        partidasCentavos: [],
    });
    const [isBolsaCentavosModalOpen, setIsBolsaCentavosModalOpen] = useState(false);
    const [configQuick, setConfigQuick] = useState({
        montoMensual: 0,
        consumoSubtotal: 0,
        cuotaExtrasCount: 0,
        cuotaExtrasSum: 0,
        totalEstimado: 0,
        tipoCalculo: 'igual',
        corteDia: '',
        sancionesInicioDia: '',
        consumoTipo: 'no',
        discountEnabled: false,
        discountPercent: 0,
        discountDescription: '',
        interesesEnabled: false,
        interesesType: 'fixed',
        interesesValue: 0,
        mesesPermitidosDeuda: 1,
        multasEnabled: false,
        multasCount: 0,
        multasList: [],
        restriccionServiciosEnabled: false,
        restriccionServicios: [],
        restriccionMeses: '',
        annualBudgetEnabled: false,
        annualBudget: 0,
        maintenanceAllocation: '',
        reserveAllocation: '',
        fondoReservaEnabled: false,
        fondoReservaPercent: 0,
        fundTargetPercent: 0,
        fundUsePolicy: '',
    });
    const [showConfigDetails, setShowConfigDetails] = useState(false);
    const [loading, setLoading] = useState(true);
    const [calculatingCharges, setCalculatingCharges] = useState(false);
    const [chargeResult, setChargeResult] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState('cuotas'); // 'cuotas' | 'sanciones' | 'marcar_historial'
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successDetails, setSuccessDetails] = useState(null);
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [showReporteMensualModal, setShowReporteMensualModal] = useState(false);
    const [reporteDetallado, setReporteDetallado] = useState(null);
    const [reporteLoading, setReporteLoading] = useState(false);
    const [reporteError, setReporteError] = useState(null);
    const [selectedMonthReport, setSelectedMonthReport] = useState(new Date().getMonth() + 1);
    const [selectedYearReport, setSelectedYearReport] = useState(new Date().getFullYear());
    const [selectedMonthHistorial, setSelectedMonthHistorial] = useState(new Date().getMonth() + 1);
    const [selectedYearHistorial, setSelectedYearHistorial] = useState(new Date().getFullYear());
    const [activeReportTab, setActiveReportTab] = useState('ingresos-egresos');
    const [saldoInicial, setSaldoInicial] = useState('');
    const [saldoModal, setSaldoModal] = useState({ open: false, title: '', message: '', type: 'success' });
    const [notasReporte, setNotasReporte] = useState('');
    const [multasManual, setMultasManual] = useState([]);
    const [multasConfig, setMultasConfig] = useState(null);
    const [casasLista, setCasasLista] = useState([]);
    const [tieneCambiosSinGuardar, setTieneCambiosSinGuardar] = useState(false);
    const [confirmCloseModal, setConfirmCloseModal] = useState(false);
    const [sugerenciasMulta, setSugerenciasMulta] = useState({ rowId: null, items: [] });
    const [casasConAdeudo, setCasasConAdeudo] = useState([]);
    const [conveniosVigentes, setConveniosVigentes] = useState([]);
    const [reporteGuardado, setReporteGuardado] = useState(null);
    const [cargandoReporteGuardado, setCargandoReporteGuardado] = useState(false);
    const [seccionesImpresion, setSeccionesImpresion] = useState({
        notas: true,
        sanciones: true,
        convenios: true,
        casasAdeudo: false
    });
    const [modulosHabilitados, setModulosHabilitados] = useState({});

    const { userData, currentUser } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;

    // --- FUNCIÓN UTILITARIA ---
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
        }).format(amount || 0);
    };

    const formatPercent = (value) => {
        const num = Number(value);
        if (Number.isNaN(num)) return '0%';
        const formatted = Number.isInteger(num) ? num.toString() : num.toFixed(2);
        return `${formatted}% `;
    };

    const formatCurrencyOrText = (value) => {
        if (value === undefined || value === null || value === '') return '—';
        const num = Number(value);
        if (Number.isNaN(num)) return String(value);
        return formatCurrency(num);
    };

    const formatTimestamp = (value) => {
        if (!value) return '';
        const dateObj = value.toDate ? value.toDate() : new Date(value);
        if (Number.isNaN(dateObj.getTime())) return '';
        return dateObj.toLocaleString('es-MX');
    };

    const humanizeInteresType = (type) => {
        switch (type) {
            case 'percent_simple':
                return 'Porcentaje simple';
            case 'percent_compound':
                return 'Porcentaje compuesto';
            case 'fixed':
            default:
                return 'Monto fijo mensual';
        }
    };

    const openReport = () => {
        setIsReportOpen(true);
        setReporteDetallado(null);
        setReporteError(null);
        setActiveReportTab('ingresos-egresos');
    };

    const closeReport = () => {
        if (tieneCambiosSinGuardar || multasManual.length > 0 || notasReporte) {
            setConfirmCloseModal(true);
        } else {
            realizarCierre();
        }
    };

    const realizarCierre = () => {
        setIsReportOpen(false);
        setReporteDetallado(null);
        setReporteError(null);
        setActiveReportTab('ingresos-egresos');
        setNotasReporte('');
        setMultasManual([]);
        setMultasConfig(null);
        setCasasLista([]);
        setTieneCambiosSinGuardar(false);
        setConfirmCloseModal(false);
        setSeccionesImpresion({
            notas: true,
            sanciones: true,
            convenios: true,
            casasAdeudo: false
        });
    };

    const generateDetailedReport = async () => {
        if (!fraccionamientoId) {
            setReporteError('No se encontró el fraccionamiento');
            return;
        }

        try {
            setReporteLoading(true);
            setReporteError(null);

            const generarReporte = httpsCallable(functions, 'generarReporteDetallado');
            const result = await generarReporte({
                fraccionamientoId,
                mes: selectedMonthReport,
                anio: selectedYearReport,
                año: selectedYearReport,
            });

            setReporteDetallado(result.data);

            // Cargar configuración de multas administrativas
            console.log('🔍 Intentando cargar configuración de multas...');
            console.log('   Fraccionamiento ID:', fraccionamientoId);
            try {
                // Primero intentar leer del documento principal
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const fraccSnap = await getDoc(fraccRef);

                let multasHabilitadas = false;
                let multasDelSistema = [];

                if (fraccSnap.exists()) {
                    const fraccData = fraccSnap.data();
                    console.log('   📄 Documento del fraccionamiento cargado');
                    console.log('   🔍 Datos completos del fraccionamiento:', fraccData);

                    // 1) Detectar flag habilitado desde múltiples nombres/formatos
                    const rawEnabled = (
                        fraccData.multasEnabled ??
                        fraccData.habilitarMultasAdministrativas ??
                        fraccData?.configuracionFinanciera?.multasEnabled ??
                        fraccData?.configuracion?.configuracionFinanciera?.multasEnabled
                    );
                    multasHabilitadas = rawEnabled === true || rawEnabled === 'true' || rawEnabled === 1;
                    console.log('   ✓ Flag de multas detectado:', rawEnabled, '→ normalizado:', multasHabilitadas);

                    // 2) Detectar lista de multas desde múltiples campos
                    let listaBruta = [];
                    if (Array.isArray(fraccData.multasAdministrativasList)) {
                        listaBruta = fraccData.multasAdministrativasList;
                        console.log('   📋 Usando multasAdministrativasList (doc principal)');
                    } else if (Array.isArray(fraccData.multasList)) {
                        listaBruta = fraccData.multasList;
                        console.log('   📋 Usando multasList (doc principal)');
                    } else if (Array.isArray(fraccData?.configuracionFinanciera?.multasAdministrativasList)) {
                        listaBruta = fraccData.configuracionFinanciera.multasAdministrativasList;
                        console.log('   📋 Usando configuracionFinanciera.multasAdministrativasList');
                    }

                    // 3) Normalizar estructura de multas { descripcion?, id, monto:number }
                    multasDelSistema = (listaBruta || []).map((m, idx) => {
                        const montoNum = (() => {
                            const v = m?.monto ?? m?.amount ?? m?.valor ?? 0;
                            if (typeof v === 'number') return v;
                            if (typeof v === 'string') return parseFloat(v.replace(/,/g, '.')) || 0;
                            return 0;
                        })();
                        return {
                            id: m?.id ?? `multa - ${idx} `,
                            descripcion: m?.descripcion ?? m?.desc ?? 'Multa',
                            monto: montoNum,
                        };
                    });
                    console.log('   📊 Multas normalizadas:', multasDelSistema.length);
                }

                // Si no se encontró en el documento principal, intentar en la subcolección
                console.log('   🔄 Buscando en subcolección configuracion/configuracionFinanciera...');
                try {
                    const configRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuracion', 'configuracionFinanciera');
                    const configSnap = await getDoc(configRef);
                    console.log('   📄 ¿Existe configuracionFinanciera?', configSnap.exists());
                    if (configSnap.exists()) {
                        const configData = configSnap.data();
                        console.log('   📄 Documento de configuración financiera encontrado');
                        console.log('   🔍 Datos completos de configuracionFinanciera:', configData);
                        console.log('   🔍 ¿Tiene multasEnabled?', 'multasEnabled' in configData);
                        console.log('   🔍 Valor de multasEnabled en config:', configData.multasEnabled);

                        if (configData.multasEnabled === true) {
                            multasHabilitadas = true;
                            console.log('   ✓ multasEnabled (configuración): true');
                        }

                        // También buscar la lista de multas en la configuración
                        if (configData.multasAdministrativasList && Array.isArray(configData.multasAdministrativasList)) {
                            multasDelSistema = configData.multasAdministrativasList;
                            console.log('   📋 multasAdministrativasList en configuración:', multasDelSistema.length, 'multas');
                        }
                    } else {
                        console.log('   ❌ No existe el documento configuracionFinanciera');
                    }
                } catch (subErr) {
                    console.log('   ❌ Error al buscar en subcolección:', subErr);
                }

                // Determinar si permitir captura manual o mostrar multas del sistema
                if (multasHabilitadas && multasDelSistema.length > 0) {
                    console.log('   ✅ Sistema de multas ACTIVADO con', multasDelSistema.length, 'multas configuradas');
                } else if (multasHabilitadas && multasDelSistema.length === 0) {
                    console.log('   ⚠️ Sistema de multas ACTIVADO pero sin multas configuradas');
                } else {
                    console.log('   ℹ️ Sistema de multas DESACTIVADO - Permitiendo captura manual');
                }

                setMultasConfig({
                    habilitarMultasAdministrativas: multasHabilitadas,
                    multasAdministrativasList: multasDelSistema
                });

            } catch (err) {
                console.error('   ❌ Error al cargar configuración de multas:', err);
                console.error('   Error completo:', err.message);
                setMultasConfig({
                    habilitarMultasAdministrativas: false,
                    multasAdministrativasList: []
                });
            }

            // Cargar lista de casas para autocompletado y adeudos
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const casasSnap = await getDocs(casasRef);
                const casas = casasSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    numero: doc.data().numero || doc.data().numeroCasa || doc.data().lote || doc.id,
                    adeudo: doc.data().adeudo || 0,
                    rol: doc.data().rol || doc.data().rolAsignado || '',
                    perfil: doc.data().perfil || '',
                    esRolCaseta: doc.data().esRolCaseta || false
                }));
                setCasasLista(casas);

                // Filtrar casas con adeudo > 0 y ordenar por número (excluyendo caseta)
                const casasAdeudo = casas
                    .filter(casa => !esCasaCaseta(casa) && parseFloat(casa.adeudo) > 0)
                    .sort((a, b) => {
                        const numA = parseInt(a.numero) || 0;
                        const numB = parseInt(b.numero) || 0;
                        return numA - numB;
                    });
                setCasasConAdeudo(casasAdeudo);

                // Cargar convenios vigentes (casas con convenioActivo === true Y sin fecha de desactivación Y con adeudo > 0)
                const conveniosActivos = casasSnap.docs
                    .map(doc => {
                        const data = doc.data();
                        const adeudoActual = parseFloat(data.adeudo || 0);

                        // Solo incluir convenios que cumplan TODAS estas condiciones:
                        // 1. convenioActivo === true
                        // 2. NO tienen fecha de desactivación
                        // 3. Tienen adeudo actual > 0 (realmente están en morosidad)
                        if (data.convenioActivo === true && !data.convenioFechaDesactivacion && adeudoActual > 0) {
                            // Obtener nombre del residente
                            const residente = data.residente || data.usuario || data.nombreResidente ||
                                (data.propietario && data.propietario.nombre) ||
                                (data.contacto && data.contacto.nombre) ||
                                'Residente no registrado';

                            return {
                                id: doc.id,
                                numero: data.numero || data.numeroCasa || data.lote || doc.id,
                                residente: residente,
                                adeudo: adeudoActual,
                                adeudoInicial: data.convenioAdeudoInicial || 0,
                                fechaActivacion: data.convenioFechaActivacion ?
                                    (data.convenioFechaActivacion.toDate ?
                                        data.convenioFechaActivacion.toDate() :
                                        new Date(data.convenioFechaActivacion)) : null,
                                fechaDesactivacion: data.convenioFechaDesactivacion ?
                                    (data.convenioFechaDesactivacion.toDate ?
                                        data.convenioFechaDesactivacion.toDate() :
                                        new Date(data.convenioFechaDesactivacion)) : null,
                            };
                        }
                        return null;
                    })
                    .filter(c => c !== null)
                    .sort((a, b) => {
                        const numA = parseInt(a.numero) || 0;
                        const numB = parseInt(b.numero) || 0;
                        return numA - numB;
                    });
                setConveniosVigentes(conveniosActivos);
                console.log('🏠 Casas cargadas:', casas.length, '- Con adeudo:', casasAdeudo.length, '- Con convenio activo y adeudo:', conveniosActivos.length);
            } catch (err) {
                console.warn('No se pudieron cargar las casas:', err);
            }
        } catch (error) {
            console.error('Error generando reporte:', error);
            setReporteError(error.message || 'Error al generar el reporte');
        } finally {
            setReporteLoading(false);
        }
    };

    const formatCurrencyReport = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
        }).format(value || 0);
    };

    const handleGuardarSaldoInicial = async (desdePestañaFondo = false) => {
        // Si no es desde la pestaña "Fondo" y ya hay un reporte guardado, no permitir modificar desde el resumen
        if (!desdePestañaFondo && reporteGuardado?.contenidoHtml) {
            setSaldoModal({
                open: true,
                title: 'Reporte ya guardado',
                message: 'Este reporte ya fue guardado. Para actualizar el saldo inicial, usa la pestaña "Fondo" y presiona "Guardar Saldo Final de Fondo".',
                type: 'error',
            });
            return;
        }

        if (!saldoInicial || Number(saldoInicial) === 0) {
            setSaldoModal({
                open: true,
                title: 'Campo requerido',
                message: 'Por favor, ingresa el saldo inicial para poder guardar.',
                type: 'error',
            });
            return;
        }

        if (!fraccionamientoId) {
            setSaldoModal({
                open: true,
                title: 'Error',
                message: 'Error: No se pudo identificar el fraccionamiento.',
                type: 'error',
            });
            return;
        }

        try {
            const ingresosArr = (reporteDetallado?.ingresos || []);
            const egresosArr = (reporteDetallado?.egresos || []);
            const totalIngresos = ingresosArr.reduce((sum, i) => sum + (Number(i.monto) || 0), 0);
            const totalEgresos = egresosArr.reduce((sum, e) => sum + (Number(e.monto) || 0), 0);
            const saldoInicialNum = Number(saldoInicial) || 0;
            const saldoFinal = saldoInicialNum + totalIngresos - totalEgresos;

            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const currentMonth = selectedMonthReport;
            const currentYear = selectedYearReport;

            // Guardar en el fraccionamiento
            await updateDoc(fraccRef, {
                [`saldoFondo_${currentYear}_${currentMonth}`]: saldoFinal,
                [`saldoFondoFecha_${currentYear}_${currentMonth}`]: serverTimestamp(),
                [`saldoFondoInicial_${currentYear}_${currentMonth}`]: saldoInicialNum,
                [`saldoFondoIngresos_${currentYear}_${currentMonth}`]: totalIngresos,
                [`saldoFondoEgresos_${currentYear}_${currentMonth}`]: totalEgresos,
            });

            // Si hay un reporte guardado y se está actualizando desde la pestaña "Fondo", actualizar también el reporte guardado
            if (desdePestañaFondo && reporteGuardado?.contenidoHtml) {
                try {
                    const docId = `${selectedYearReport}_${String(selectedMonthReport).padStart(2, '0')}`;
                    const reporteRef = doc(db, 'fraccionamientos', fraccionamientoId, 'reportesFinancieros', docId);
                    const reporteSnap = await getDoc(reporteRef);

                    if (reporteSnap.exists()) {
                        const reporteData = reporteSnap.data();
                        // Actualizar el resumenMeta con el nuevo saldo inicial
                        const resumenMetaActualizado = {
                            ...(reporteData.resumenMeta || {}),
                            saldoInicial: saldoInicialNum,
                            saldoFinal: saldoFinal,
                        };

                        await updateDoc(reporteRef, {
                            resumenMeta: resumenMetaActualizado,
                        });

                        // Actualizar el estado local del reporte guardado
                        setReporteGuardado(prev => ({
                            ...prev,
                            resumenMeta: resumenMetaActualizado,
                        }));

                        console.log('✅ Reporte guardado actualizado con nuevo saldo inicial:', saldoInicialNum);
                    }
                } catch (error) {
                    console.error('Error actualizando reporte guardado:', error);
                    // Continuar aunque falle la actualización del reporte guardado
                }
            }

            setSaldoModal({
                open: true,
                title: 'Saldo guardado correctamente',
                message: desdePestañaFondo && reporteGuardado?.contenidoHtml
                    ? `Se actualizó el Saldo Inicial de Fondo para ${getMonthLabel(selectedMonthReport)}. El reporte guardado también fue actualizado con el nuevo valor.`
                    : `Se guardó el Saldo Inicial de Fondo para ${getMonthLabel(selectedMonthReport)}. Este valor es específico para este mes y no afectará los reportes de meses anteriores.`,
                type: 'success',
            });
        } catch (error) {
            console.error('Error guardando saldo inicial:', error);
            setSaldoModal({
                open: true,
                title: 'Error al guardar',
                message: 'No se pudo guardar el saldo inicial. Intenta nuevamente.',
                type: 'error',
            });
        }
    };

    const getMonthLabel = (monthNumber) => {
        return new Date(selectedYearReport, monthNumber - 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    };

    // Helper: Contar movimientos registrados en memoria para el mes/año seleccionado
    const contarMovimientosMes = (mes, anio) => {
        let countIngresos = 0;
        let countEgresos = 0;

        (ingresos || []).forEach(ing => {
            if (ing.deleted || ing.metodoPago === 'registro_automatico') return;
            const fechaStr = ing.fecha || ing.fechaPago || (ing.createdAt?.toDate ? ing.createdAt.toDate().toISOString() : '');
            if (!fechaStr) return;
            const d = new Date(fechaStr);
            if (!isNaN(d.getTime()) && (d.getMonth() + 1) === mes && d.getFullYear() === anio) {
                countIngresos++;
            }
        });

        (egresos || []).forEach(eg => {
            if (eg.deleted) return;
            const fechaStr = eg.fecha || (eg.createdAt?.toDate ? eg.createdAt.toDate().toISOString() : '');
            if (!fechaStr) return;
            const d = new Date(fechaStr);
            if (!isNaN(d.getTime()) && (d.getMonth() + 1) === mes && d.getFullYear() === anio) {
                countEgresos++;
            }
        });

        return { countIngresos, countEgresos, total: countIngresos + countEgresos };
    };

    // Helper: Aplicar atajos rápidos de período
    const aplicarAtajoPeriodoReporte = (tipo) => {
        const ahora = new Date();
        if (tipo === 'actual') {
            setSelectedMonthReport(ahora.getMonth() + 1);
            setSelectedYearReport(ahora.getFullYear());
        } else if (tipo === 'anterior') {
            const mesAnt = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
            setSelectedMonthReport(mesAnt.getMonth() + 1);
            setSelectedYearReport(mesAnt.getFullYear());
        } else if (tipo === 'haceDos') {
            const mesDos = new Date(ahora.getFullYear(), ahora.getMonth() - 2, 1);
            setSelectedMonthReport(mesDos.getMonth() + 1);
            setSelectedYearReport(mesDos.getFullYear());
        }
    };

    // Helper: Determinar si un atajo coincide con el período actualmente seleccionado
    const esAtajoActivo = (tipo) => {
        const ahora = new Date();
        if (tipo === 'actual') {
            return selectedMonthReport === (ahora.getMonth() + 1) && selectedYearReport === ahora.getFullYear();
        }
        if (tipo === 'anterior') {
            const mesAnt = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
            return selectedMonthReport === (mesAnt.getMonth() + 1) && selectedYearReport === mesAnt.getFullYear();
        }
        if (tipo === 'haceDos') {
            const mesDos = new Date(ahora.getFullYear(), ahora.getMonth() - 2, 1);
            return selectedMonthReport === (mesDos.getMonth() + 1) && selectedYearReport === mesDos.getFullYear();
        }
        return false;
    };

    // Cargar reporte guardado y saldo inicial del mes específico
    useEffect(() => {
        if (!fraccionamientoId) {
            setReporteGuardado(null);
            setSaldoInicial('');
            return;
        }

        const cargarDatosMes = async () => {
            setCargandoReporteGuardado(true);
            try {
                const docId = `${selectedYearReport}_${String(selectedMonthReport).padStart(2, '0')}`;
                const reporteRef = doc(db, 'fraccionamientos', fraccionamientoId, 'reportesFinancieros', docId);
                const snap = await getDoc(reporteRef);

                if (snap.exists()) {
                    const data = snap.data();
                    setReporteGuardado({ id: docId, ...data });

                    // Si hay un reporte guardado, usar el saldo inicial del reporte guardado (estático)
                    if (data.resumenMeta && data.resumenMeta.saldoInicial !== undefined) {
                        setSaldoInicial(String(data.resumenMeta.saldoInicial));
                        console.log('📊 Usando saldo inicial del reporte guardado:', data.resumenMeta.saldoInicial);
                    } else {
                        // Si no hay saldo en el reporte guardado, intentar cargar del fraccionamiento
                        await cargarSaldoInicialDelMes();
                    }
                } else {
                    setReporteGuardado(null);
                    // Si no hay reporte guardado, cargar el saldo inicial del mes desde el fraccionamiento
                    await cargarSaldoInicialDelMes();
                }
            } catch (err) {
                console.error('No se pudo cargar el reporte guardado:', err);
                setReporteGuardado(null);
                await cargarSaldoInicialDelMes();
            } finally {
                setCargandoReporteGuardado(false);
            }
        };

        const cargarSaldoInicialDelMes = async () => {
            try {
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const fraccSnap = await getDoc(fraccRef);
                if (fraccSnap.exists()) {
                    const fraccData = fraccSnap.data();
                    const saldoKey = `saldoFondoInicial_${selectedYearReport}_${selectedMonthReport}`;
                    const saldoGuardado = fraccData[saldoKey];

                    if (saldoGuardado !== undefined && saldoGuardado !== null) {
                        setSaldoInicial(String(saldoGuardado));
                        console.log(`📊 Saldo inicial cargado del mes ${selectedMonthReport}/${selectedYearReport}:`, saldoGuardado);
                    } else {
                        // Si no hay saldo guardado para este mes, dejar vacío para que el usuario lo ingrese
                        setSaldoInicial('');
                        console.log(`📊 No hay saldo inicial guardado para ${selectedMonthReport}/${selectedYearReport}`);
                    }
                }
            } catch (err) {
                console.error('Error cargando saldo inicial del mes:', err);
                setSaldoInicial('');
            }
        };

        cargarDatosMes();
    }, [fraccionamientoId, selectedMonthReport, selectedYearReport]);

    // Funciones para multas administrativas
    const agregarMulta = () => {
        setMultasManual([...multasManual, { id: Date.now(), casa: '', infraccion: '', fecha: '', monto: '' }]);
        setTieneCambiosSinGuardar(true);
    };

    const eliminarMulta = (id) => {
        setMultasManual(prev => prev.filter(m => m.id !== id));
    };

    const actualizarMulta = (id, campo, valor) => {
        setMultasManual(prev => prev.map(m => m.id === id ? { ...m, [campo]: valor } : m));
    };

    // Utilidad: normaliza texto (minúsculas, sin acentos, espacios simples)
    const normalizeTxt = (s) => (s || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    // Autocompletar infracción y asignar monto según catálogo con coincidencia flexible
    const handleInfraccionInput = (id, valor) => {
        // Primero actualizamos el valor tal cual lo escribe el usuario
        setMultasManual(prev => prev.map(m => m.id === id ? { ...m, infraccion: valor } : m));
        setTieneCambiosSinGuardar(true);

        try {
            const lista = (multasConfig && Array.isArray(multasConfig.multasAdministrativasList))
                ? multasConfig.multasAdministrativasList
                : [];
            if (!lista.length) {
                setSugerenciasMulta({ rowId: null, items: [] });
                return;
            }

            const raw = (valor || '').trim();
            const vNorm = normalizeTxt(raw);

            // Si el campo está vacío, solo ocultar sugerencias
            if (raw.length === 0) {
                setSugerenciasMulta({ rowId: null, items: [] });
                return;
            }

            // 1) Coincidencia directa por id
            let match = lista.find(m => String(m?.id) === raw);

            // 2) Coincidencia exacta por descripción (normalizada)
            if (!match) {
                match = lista.find(m => normalizeTxt(m?.descripcion) === vNorm);
            }

            // 3) Candidatos por prefijo o inclusión cuando >= 1 letra
            let candidates = [];
            if (vNorm.length >= 1) {
                candidates = lista.filter(m => {
                    const d = normalizeTxt(m?.descripcion);
                    return d.startsWith(vNorm) || d.includes(vNorm);
                });
            }

            // Solo actualizar dropdown de sugerencias - NO auto-completar
            setSugerenciasMulta({ rowId: id, items: candidates.slice(0, 6) });
        } catch (e) {
            setSugerenciasMulta({ rowId: null, items: [] });
        }
    };

    const calcularTotalMultas = () => {
        return multasManual.reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0);
    };

    const isCurrentSelection = () => {
        const today = new Date();
        return selectedYearReport === today.getFullYear() && selectedMonthReport === (today.getMonth() + 1);
    };

    const autoGuardarReporte = async (contenidoHtml, resumenMeta = {}) => {
        if (!fraccionamientoId || !contenidoHtml) return;

        try {
            const docId = `${selectedYearReport}_${String(selectedMonthReport).padStart(2, '0')}`;
            const reporteRef = doc(db, 'fraccionamientos', fraccionamientoId, 'reportesFinancieros', docId);
            const payload = {
                contenidoHtml,
                mes: selectedMonthReport,
                año: selectedYearReport,
                notas: notasReporte || '',
                multasManual: multasManual || [],
                casasConAdeudo: casasConAdeudo || [],
                resumenMeta,
                guardadoEn: serverTimestamp(),
            };

            await setDoc(reporteRef, payload, { merge: true });
            setReporteGuardado({ id: docId, ...payload, guardadoEn: new Date().toISOString() });
        } catch (err) {
            console.error('No se pudo autoguardar el reporte mensual:', err);
        }
    };

    const handleExportReport = async () => {
        if (!reporteDetallado || !fraccionamientoId) return;

        try {
            // Cargar información del fraccionamiento
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fraccSnap = await getDoc(fraccRef);

            let fraccionamientoNombre = 'N/A';
            let fraccionamientoDireccion = '';
            let logoUrl = '';
            let selloUrl = '';
            let adminNombre = '';

            if (fraccSnap.exists()) {
                const fraccData = fraccSnap.data();

                // Obtener configuración de recibos (prioridad)
                const configRecibos = fraccData.configuracionRecibos || {};

                // Obtener logo y sello desde configuracionRecibos
                logoUrl = configRecibos.logoUrl || fraccData.logoUrl || '';
                selloUrl = configRecibos.selloUrl || fraccData.selloUrl || '';

                // Obtener nombre del fraccionamiento (prioridad: configuracionRecibos > root level)
                fraccionamientoNombre = configRecibos.condominioNombre || fraccData.condominioNombre || fraccData.nombre || 'N/A';
                fraccionamientoDireccion = configRecibos.condominioDireccion || fraccData.condominioDireccion || '';
                adminNombre = configRecibos.administradorNombre || fraccData.adminNombre || '';

                // Debug: mostrar las URLs obtenidas
                console.log('📄 Datos del fraccionamiento cargados:');
                console.log('  - Nombre:', fraccionamientoNombre);
                console.log('  - Dirección:', fraccionamientoDireccion);
                console.log('  - Logo URL:', logoUrl || '(no configurado)');
                console.log('  - Sello URL:', selloUrl || '(no configurado)');
                console.log('  - Admin:', adminNombre);
                console.log('  - Config Recibos:', configRecibos);
            }

            const mesLabel = getMonthLabel(selectedMonthReport);
            const fecha = new Date().toLocaleDateString('es-MX');

            // Filtrar egresos en fijos y variables
            const egresosFijos = (reporteDetallado.egresos || []).filter((e) => {
                const tipo = (e.tipoGasto || e.tipo || '').toLowerCase();
                const cat = (e.categoria || '').toLowerCase();
                return tipo === 'fijo' || cat.includes('fijo');
            });

            const egresosVariables = (reporteDetallado.egresos || []).filter((e) => {
                const tipo = (e.tipoGasto || e.tipo || '').toLowerCase();
                const cat = (e.categoria || '').toLowerCase();
                return tipo === 'variable' || (tipo !== 'fijo' && !cat.includes('fijo'));
            });

            // Calcular totales filtrando adeudos y cargos automáticos
            const totalIngresos = (reporteDetallado.ingresos || [])
                .filter(ing => {
                    const concepto = String(ing.concepto || ing.descripcion || ing.categoria || '').toLowerCase();
                    const metodoPago = String(ing.metodoPago || '').toLowerCase();
                    if (metodoPago === 'registro_automatico' ||
                        concepto.includes('adeudo') ||
                        concepto.includes('sin adeudo') ||
                        concepto.includes('saldo final de mes') ||
                        concepto.includes('recargo por morosidad')) {
                        return false;
                    }
                    const monto = Number(ing.montoNeto ?? ing.monto ?? 0);
                    return monto > 0;
                })
                .reduce((sum, ing) => sum + (Number(ing.montoNeto ?? ing.monto) || 0), 0) || 0;
            const totalEgresosFijos = egresosFijos.reduce((sum, e) => sum + (Number(e.monto) || 0), 0);
            const totalEgresosVariables = egresosVariables.reduce((sum, e) => sum + (Number(e.monto) || 0), 0);
            const totalEgresos = totalEgresosFijos + totalEgresosVariables;
            // Usar el saldo inicial del reporte guardado si existe (estático), sino usar el del input
            let saldoInicialParaReporte = Number(saldoInicial) || 0;
            if (reporteGuardado && reporteGuardado.resumenMeta && reporteGuardado.resumenMeta.saldoInicial !== undefined) {
                // Si hay un reporte guardado, usar su saldo inicial (estático, no se modifica)
                saldoInicialParaReporte = Number(reporteGuardado.resumenMeta.saldoInicial) || 0;
                console.log('📊 Usando saldo inicial del reporte guardado (estático):', saldoInicialParaReporte);
            } else {
                // Si no hay reporte guardado, usar el saldo inicial del input (del mes específico)
                saldoInicialParaReporte = Number(saldoInicial) || 0;
                console.log('📊 Usando saldo inicial del input (mes actual):', saldoInicialParaReporte);
            }

            const saldoFinal = saldoInicialParaReporte + totalIngresos - totalEgresos;
            const totalAdeudoCasas = casasConAdeudo.reduce((sum, c) => sum + (parseFloat(c.adeudo) || 0), 0);

            // Generar filas de ingresos agrupados
            const ingresosAgrupados = (reporteDetallado.ingresos || []).reduce((acc, ing) => {
                const metodoPago = String(ing.metodoPago || '').toLowerCase();
                const conceptoLower = String(ing.concepto || ing.descripcion || ing.categoria || '').toLowerCase();

                // Excluir cargos automáticos del sistema, conceptos de adeudo y cuotas no cobradas
                if (metodoPago === 'registro_automatico' ||
                    conceptoLower.includes('adeudo') ||
                    conceptoLower.includes('sin adeudo') ||
                    conceptoLower.includes('saldo final de mes') ||
                    conceptoLower.includes('recargo por morosidad')) {
                    return acc;
                }

                const agregarPartida = (conc, monto) => {
                    if (monto <= 0) return;
                    const existing = acc.find(item => item.concepto === conc);
                    if (existing) {
                        existing.monto += monto;
                    } else {
                        acc.push({ concepto: conc, monto });
                    }
                };

                if (Array.isArray(ing.pagos) && ing.pagos.length > 0) {
                    ing.pagos.forEach(partida => {
                        const concPartida = (partida.concepto || 'Sin concepto').trim();
                        const montoPartida = Number(partida.montoNeto ?? partida.monto ?? 0);
                        agregarPartida(concPartida, montoPartida);
                    });
                } else {
                    const concepto = (ing.concepto || ing.descripcion || ing.categoria || 'Sin concepto').trim();
                    const monto = Number(ing.montoNeto ?? ing.monto) || 0;
                    agregarPartida(concepto, monto);
                }

                return acc;
            }, []);

            const ingresosFilas = ingresosAgrupados.length > 0
                ? ingresosAgrupados.map(item => '<tr><td>' + item.concepto + '</td><td class="text-right">' + formatCurrencyReport(item.monto) + '</td></tr>').join('')
                : '<tr><td colspan="2">No hay ingresos</td></tr>';

            const renderEgresoRow = (egr) => {
                const proveedor = egr.proveedor ? egr.proveedor.trim() : '';
                const descripcion = (egr.descripcion || egr.concepto || 'Sin descripción').trim();
                let celdaDescripcion = '';

                if (proveedor) {
                    celdaDescripcion = `<strong>${proveedor}</strong><br><span style="font-size: 0.85em; color: #555;">${descripcion}</span>`;
                } else {
                    celdaDescripcion = descripcion;
                }

                return `<tr><td>${celdaDescripcion}</td><td class="text-right">${formatCurrencyReport(egr.monto)}</td></tr>`;
            };

            const egresosFijosFilas = egresosFijos.length > 0
                ? egresosFijos.map(renderEgresoRow).join('')
                : '<tr><td colspan="2">No hay egresos fijos</td></tr>';

            const egresosVariablesFilas = egresosVariables.length > 0
                ? egresosVariables.map(renderEgresoRow).join('')
                : '<tr><td colspan="2">No hay egresos variables</td></tr>';

            const notasSeccion = notasReporte
                ? '<div class="section"><h2>Notas</h2><div class="notas">' + notasReporte.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</div></div>'
                : '';

            // Preparar sección de sanciones si hay sanciones registradas
            const multasSeccion = multasManual.length > 0
                ? '<div class="section"><h2>Sanciones Administrativas</h2><table><thead><tr><th>Casa</th><th>Infracción</th><th>Fecha</th><th class="text-right">Monto</th></tr></thead><tbody>' +
                multasManual.map(m => '<tr><td>' + (m.casa || 'N/A') + '</td><td>' + (m.infraccion || 'N/A') + '</td><td>' + (m.fecha ? new Date(m.fecha + 'T00:00:00').toLocaleDateString('es-MX') : 'N/A') + '</td><td class="text-right">' + formatCurrencyReport(parseFloat(m.monto) || 0) + '</td></tr>').join('') +
                '<tr class="total-row"><td colspan="3">Total Sanciones</td><td class="text-right">' + formatCurrencyReport(calcularTotalMultas()) + '</td></tr></tbody></table></div>'
                : '';

            // Preparar sección de convenios vigentes en dos columnas
            const conveniosSeccion = conveniosVigentes.length > 0
                ? (() => {
                    const mitad = Math.ceil(conveniosVigentes.length / 2);
                    const columna1 = conveniosVigentes.slice(0, mitad);
                    const columna2 = conveniosVigentes.slice(mitad);

                    const generarFilas = (convenios) => {
                        return convenios.map(conv => {
                            const fechaInicio = conv.fechaActivacion ?
                                (conv.fechaActivacion.toDate ? conv.fechaActivacion.toDate() : new Date(conv.fechaActivacion)).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' }) :
                                'N/A';
                            const estado = conv.fechaDesactivacion ? 'Finalizado' : 'Activo';
                            return '<tr><td>Casa ' + conv.numero + '</td><td>' + fechaInicio + '</td><td>' + estado + '</td></tr>';
                        }).join('');
                    };

                    return '<div class="section"><h2>Convenios Vigentes</h2><div class="two-columns-table"><div class="table-column"><table><thead><tr><th>No. Casa</th><th>Fecha Inicio</th><th>Estado</th></tr></thead><tbody>' +
                        generarFilas(columna1) +
                        '</tbody></table></div><div class="table-column"><table><thead><tr><th>No. Casa</th><th>Fecha Inicio</th><th>Estado</th></tr></thead><tbody>' +
                        generarFilas(columna2) +
                        '</tbody></table></div></div></div>';
                })()
                : '';

            // Preparar sección de casas con adeudo en dos columnas
            const casasAdeudoSeccion = casasConAdeudo.length > 0
                ? (() => {
                    const mitad = Math.ceil(casasConAdeudo.length / 2);
                    const columna1 = casasConAdeudo.slice(0, mitad);
                    const columna2 = casasConAdeudo.slice(mitad);
                    const totalAdeudo = casasConAdeudo.reduce((sum, c) => sum + parseFloat(c.adeudo), 0);

                    const generarFilas = (casas) => {
                        return casas.map(casa => '<tr><td>Casa ' + casa.numero + '</td><td class="text-right">' + formatCurrencyReport(parseFloat(casa.adeudo)) + '</td></tr>').join('');
                    };

                    return '<div class="section"><h2>Casas con Adeudo</h2><div class="two-columns-table"><div class="table-column"><table><thead><tr><th>No. Casa</th><th class="text-right">Adeudo</th></tr></thead><tbody>' +
                        generarFilas(columna1) +
                        '</tbody></table></div><div class="table-column"><table><thead><tr><th>No. Casa</th><th class="text-right">Adeudo</th></tr></thead><tbody>' +
                        generarFilas(columna2) +
                        '</tbody></table></div></div><div class="total-row-container"><table style="width: 100%; margin-top: 10px;"><tr class="total-row"><td style="text-align: left; padding: 8px; font-weight: bold;">TOTAL ADEUDADO</td><td class="text-right" style="padding: 8px; font-weight: bold;">' + formatCurrencyReport(totalAdeudo) + '</td></tr></table></div></div>';
                })()
                : '';

            // Convertir imágenes a base64 usando Cloud Function para evitar problemas de CORS
            let logoHtml = '';
            let selloHtml = '';

            // Función auxiliar para convertir URL a base64 usando Cloud Function (evita CORS completamente)
            const urlToBase64ViaFunction = async (url, tipo = 'imagen') => {
                if (!url || typeof url !== 'string') {
                    console.warn(`[${tipo}] URL vacía o inválida`);
                    return null;
                }

                try {
                    console.log(`[${tipo}] Llamando a Cloud Function para convertir imagen...`);
                    const convertImage = httpsCallable(functions, 'convertImageToBase64');
                    const result = await convertImage({ imageUrl: url });

                    if (result.data && result.data.base64) {
                        console.log(`[${tipo}] ✅ Imagen convertida a base64 exitosamente`);
                        return result.data.base64;
                    } else {
                        console.warn(`[${tipo}] ⚠️ Cloud Function no retornó base64`);
                        return null;
                    }
                } catch (error) {
                    console.error(`[${tipo}] ❌ Error al llamar Cloud Function:`, error);
                    return null;
                }
            };

            // Convertir logo a base64 si existe
            if (logoUrl) {
                console.log('🖼️ Procesando logo...');
                try {
                    const logoBase64 = await urlToBase64ViaFunction(logoUrl, 'Logo');
                    if (logoBase64) {
                        logoHtml = '<img src="' + logoBase64 + '" alt="Logo" class="header-logo" />';
                        console.log('✅ Logo cargado exitosamente');
                    } else {
                        console.warn('⚠️ No se pudo cargar el logo, se omitirá del reporte');
                    }
                } catch (error) {
                    console.error('❌ Error al procesar logo:', error);
                }
            } else {
                console.log('ℹ️ No hay URL de logo configurada');
            }

            // Convertir sello a base64 si existe
            if (selloUrl) {
                console.log('🖼️ Procesando sello...');
                try {
                    const selloBase64 = await urlToBase64ViaFunction(selloUrl, 'Sello');
                    if (selloBase64) {
                        selloHtml = '<img src="' + selloBase64 + '" alt="Sello" class="signature-seal" />';
                        console.log('✅ Sello cargado exitosamente');
                    } else {
                        console.warn('⚠️ No se pudo cargar el sello, se omitirá del reporte');
                    }
                } catch (error) {
                    console.error('❌ Error al procesar sello:', error);
                }
            } else {
                console.log('ℹ️ No hay URL de sello configurada');
            }

            // Crear contenido HTML para impresión con el nuevo orden
            const contenidoHtml = '<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Estado de Cuenta Mensual - ' + mesLabel + '</title><style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; line-height: 1.3; color: #333; background: white; font-size: 11px; } .container { max-width: 900px; margin: 0 auto; padding: 20px; } .header { position: relative; text-align: center; border-bottom: 2px solid #0066cc; padding-bottom: 12px; margin-bottom: 18px; min-height: 70px; } .header-logo { position: absolute; top: 0; right: 0; max-width: 100px; max-height: 65px; object-fit: contain; } .header h1 { font-size: 18px; color: #0066cc; margin-bottom: 4px; padding-right: 110px; } .header p { color: #666; font-size: 10px; margin: 2px 0; } .section { margin-bottom: 18px; page-break-inside: avoid; } .section h2 { font-size: 14px; color: #1f2937; border-bottom: 1.5px solid #e5e5e5; padding-bottom: 6px; margin-bottom: 10px; } table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10px; } th { background-color: #f3f4f6; padding: 6px 8px; text-align: left; font-weight: 600; border-bottom: 1.5px solid #e5e5e5; color: #374151; font-size: 10px; } td { padding: 5px 8px; border-bottom: 1px solid #e5e5e5; } .text-right { text-align: right; } .total-row { font-weight: bold; background-color: #f9fafb; border-top: 1.5px solid #e5e5e5; } .summary { background-color: #f8f9fa; border-left: 3px solid #0066cc; padding: 10px; margin: 10px 0; border-radius: 3px; font-size: 10px; } .summary-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #e5e5e5; } .summary-row:last-child { border-bottom: none; } .summary-label { font-weight: 500; } .summary-value { font-weight: bold; min-width: 100px; text-align: right; } .positive { color: #16a34a; } .negative { color: #dc2626; } .notas { background-color: #fffbeb; border-left: 3px solid #f59e0b; padding: 10px; margin: 10px 0; border-radius: 3px; white-space: pre-wrap; word-wrap: break-word; font-size: 10px; } .footer { margin-top: 25px; text-align: center; border-top: 1.5px solid #e5e5e5; padding-top: 12px; font-size: 9px; color: #666; } .signature { margin-top: 30px; text-align: center; page-break-inside: avoid; } .signature-seal { max-width: 120px; max-height: 120px; margin: 0 auto 8px; display: block; } .signature-name { font-weight: 600; font-size: 11px; color: #333; margin-top: 8px; } .total-summary { background-color: #fff; border: 1.5px solid #e5e5e5; padding: 12px; border-radius: 5px; margin: 12px 0; } .total-summary table { margin: 0; } .two-columns-table { display: flex; gap: 15px; width: 100%; } .table-column { flex: 1; } .table-column table { width: 100%; font-size: 9px; } .table-column th { padding: 5px 6px; font-size: 9px; } .table-column td { padding: 4px 6px; font-size: 9px; } .total-row-container { margin-top: 10px; } @media print { body { margin: 0; padding: 0; font-size: 10px; } .container { padding: 15px; } .section { page-break-inside: avoid; } .signature { page-break-before: avoid; } .two-columns-table { display: flex; gap: 10px; } .table-column { flex: 1; } }</style></head><body><div class="container"><div class="header">' + logoHtml + '<h1>Estado de Cuenta Mensual</h1><p><strong>Fraccionamiento:</strong> ' + fraccionamientoNombre + '</p>' + (fraccionamientoDireccion ? '<p><strong>Dirección:</strong> ' + fraccionamientoDireccion + '</p>' : '') + '<p><strong>Período:</strong> ' + mesLabel + '</p><p><strong>Fecha de Generación:</strong> ' + fecha + '</p></div>' +

                // 1. INGRESOS DEL MES
                '<div class="section"><h2>Ingresos del mes</h2><table><thead><tr><th>Concepto</th><th class="text-right">Monto</th></tr></thead><tbody>' + ingresosFilas + '<tr class="total-row"><td>Total Ingresos</td><td class="text-right">' + formatCurrencyReport(totalIngresos) + '</td></tr></tbody></table></div>' +

                // 2. EGRESOS FIJOS
                '<div class="section"><h2>Egresos Fijos</h2><table><thead><tr><th>Descripción</th><th class="text-right">Monto</th></tr></thead><tbody>' + egresosFijosFilas + '<tr class="total-row"><td>Total Egresos Fijos</td><td class="text-right">' + formatCurrencyReport(totalEgresosFijos) + '</td></tr></tbody></table></div>' +

                // 3. EGRESOS VARIABLES
                '<div class="section"><h2>Egresos Variables</h2><table><thead><tr><th>Descripción</th><th class="text-right">Monto</th></tr></thead><tbody>' + egresosVariablesFilas + '<tr class="total-row"><td>Total Egresos Variables</td><td class="text-right">' + formatCurrencyReport(totalEgresosVariables) + '</td></tr></tbody></table></div>' +

                // 4. TOTAL DE EGRESOS
                '<div class="section"><h2>Total de Egresos</h2><div class="total-summary"><table><tbody><tr style="border-bottom: 1px solid #e5e5e5;"><td style="padding: 8px 0;">Egresos Fijos</td><td class="text-right" style="padding: 8px 0; font-weight: 600;">' + formatCurrencyReport(totalEgresosFijos) + '</td></tr><tr style="border-bottom: 1px solid #e5e5e5;"><td style="padding: 8px 0;">Egresos Variables</td><td class="text-right" style="padding: 8px 0; font-weight: 600;">' + formatCurrencyReport(totalEgresosVariables) + '</td></tr><tr><td style="padding: 8px 0; font-weight: bold; font-size: 16px;">Total Egresos</td><td class="text-right" style="padding: 8px 0; font-weight: bold; font-size: 16px; color: #dc2626;">' + formatCurrencyReport(totalEgresos) + '</td></tr></tbody></table></div></div>' +

                // 5. RESUMEN DE FINANZAS
                '<div class="section"><h2>Resumen de Finanzas</h2><div class="summary"><div class="summary-row"><span class="summary-label">Saldo Inicial:</span><span class="summary-value">' + formatCurrencyReport(saldoInicialParaReporte) + '</span></div><div class="summary-row"><span class="summary-label positive">+ Total Ingresos:</span><span class="summary-value positive">' + formatCurrencyReport(totalIngresos) + '</span></div><div class="summary-row"><span class="summary-label negative">- Total Egresos:</span><span class="summary-value negative">' + formatCurrencyReport(totalEgresos) + '</span></div><div class="summary-row"><span class="summary-label" style="font-size: 16px; font-weight: 600;">Saldo Final de Fondo:</span><span class="summary-value" style="font-size: 16px; font-weight: 700; color: ' + (saldoFinal >= 0 ? '#16a34a' : '#dc2626') + '">' + formatCurrencyReport(saldoFinal) + '</span></div></div></div>' +

                // 6. NOTAS
                (seccionesImpresion.notas ? notasSeccion : '') +

                // 7. SANCIONES ADMINISTRATIVAS
                (seccionesImpresion.sanciones ? multasSeccion : '') +

                // 8. CONVENIOS VIGENTES
                (seccionesImpresion.convenios ? conveniosSeccion : '') +

                // 9. CASAS CON ADEUDO
                (seccionesImpresion.casasAdeudo ? casasAdeudoSeccion : '') +

                // 10. FIRMA Y SELLO
                (selloUrl || adminNombre ? '<div class="signature">' + selloHtml + (adminNombre ? '<p class="signature-name">ATT Admin: ' + adminNombre + '</p>' : '') + '</div>' : '') +

                // 11. FOOTER
                '<div class="footer"><p>Este documento fue generado automáticamente por el equipo de Domus Habita.</p></div></div></body></html>';

            await autoGuardarReporte(contenidoHtml, {
                totalIngresos,
                totalEgresosFijos,
                totalEgresosVariables,
                totalEgresos,
                saldoInicial: saldoInicialParaReporte, // Guardar el saldo inicial que se usó (estático)
                saldoFinal,
                totalMultas: calcularTotalMultas(),
                totalCasasAdeudo: totalAdeudoCasas,
            });

            // Abrir en nueva ventana para imprimir
            const ventana = window.open('', '_blank');
            ventana.document.write(contenidoHtml);
            ventana.document.close();
            ventana.print();
        } catch (error) {
            console.error('Error al exportar reporte:', error);
            alert('Error al generar el reporte. Por favor intente nuevamente.');
        }
    };

    const handleExportReporteGuardado = () => {
        if (!reporteGuardado?.contenidoHtml) {
            alert('No hay un reporte guardado para este mes.');
            return;
        }

        const ventana = window.open('', '_blank');
        ventana.document.write(reporteGuardado.contenidoHtml);
        ventana.document.close();
        ventana.print();
    };

    const handleCalcularCuotasMensuales = () => {
        if (!fraccionamientoId) {
            setErrorMessage('No se encontró el fraccionamiento');
            setShowErrorModal(true);
            return;
        }

        if (configQuick.montoMensual <= 0) {
            setErrorMessage('La cuota mensual no está configurada. Por favor, ve a Configuración Financiera y establece el monto mensual.');
            setShowErrorModal(true);
            return;
        }

        setConfirmAction('cuotas');
        setShowConfirmModal(true);
    };

    const handleConfirmCalcular = async () => {
        console.log('🔵 [CORTE] Iniciando aplicación de cargos mensuales (solo cuota)');
        console.log('🔵 [CORTE] fraccionamientoId:', fraccionamientoId);

        // Cargar y mostrar configuración financiera antes de enviar
        try {
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fraccSnap = await getDoc(fraccRef);
            if (fraccSnap.exists()) {
                const fraccData = fraccSnap.data();
                const config = fraccData.configuracionFinanciera || {};

                console.log('📊 [CORTE] Configuración Financiera detectada:');
                console.log('   💰 Cuota Mensual:', config.montoMensual || config.cuotaMensual || 0);
                console.log('   🔧 Consumo Individualizado:', {
                    habilitado: config.consumoIndividualizado || false,
                    consumoTipo: config.consumoTipo || 'no',
                    agua: { activo: config.consumoAgua || false, monto: config.consumoAguaMonto || 0 },
                    gas: { activo: config.consumoGas || false, monto: config.consumoGasMonto || 0 },
                    internet: { activo: config.consumoInternet || false, monto: config.consumoInternetMonto || 0 },
                    luz: { activo: config.consumoLuz || false, monto: config.consumoLuzMonto || 0 }
                });

                // Calcular consumo total esperado
                let consumoTotalEsperado = 0;
                if ((config.consumoTipo && config.consumoTipo !== 'no') || config.consumoIndividualizado) {
                    if (config.consumoAgua && Number(config.consumoAguaMonto || 0) > 0) {
                        consumoTotalEsperado += Number(config.consumoAguaMonto || 0);
                    }
                    if (config.consumoGas && Number(config.consumoGasMonto || 0) > 0) {
                        consumoTotalEsperado += Number(config.consumoGasMonto || 0);
                    }
                    if (config.consumoInternet && Number(config.consumoInternetMonto || 0) > 0) {
                        consumoTotalEsperado += Number(config.consumoInternetMonto || 0);
                    }
                    if (config.consumoLuz && Number(config.consumoLuzMonto || 0) > 0) {
                        consumoTotalEsperado += Number(config.consumoLuzMonto || 0);
                    }
                }
                console.log('   💵 Consumo Individualizado TOTAL esperado:', consumoTotalEsperado);
                console.log('   📈 Cuota Total esperada (Cuota + Consumo):', (config.montoMensual || config.cuotaMensual || 0) + consumoTotalEsperado);
            }
        } catch (err) {
            console.warn('⚠️ [CORTE] No se pudo cargar configuración financiera para logs:', err);
        }

        console.log('🔵 [CORTE] Parámetros enviados:', {
            fraccionamientoId: fraccionamientoId,
            forzarAplicacion: true,
            fase: 'corte' // explícito
        });

        setShowConfirmModal(false);

        setCalculatingCharges(true);
        setChargeResult(null);

        try {
            // Forzar refresh del token para asegurar últimos custom claims
            if (currentUser && currentUser.getIdToken) {
                await currentUser.getIdToken(true);
            }

            const aplicarCargosManual = httpsCallable(functions, 'aplicarCargosMensualesManual');

            const params = {
                fraccionamientoId: fraccionamientoId,
                forzarAplicacion: true,
                fase: 'corte'
            };
            console.log('🔵 [CORTE] Invocando función con params:', params);

            const result = await aplicarCargosManual(params);
            console.log('🔵 [CORTE] Resultado recibido:', result.data);

            console.log('🔵 [CORTE] Success:', result.data.success);
            console.log('🔵 [CORTE] Casas actualizadas:', result.data.casasActualizadas);
            if (result.data.detallesCasas) {
                console.log('🔵 [CORTE] Detalles de casas:', result.data.detallesCasas);
            }

            if (result.data.success) {
                setChargeResult({
                    success: true,
                    message: result.data.message,
                    casasActualizadas: result.data.casasActualizadas,
                    configuracion: result.data.configuracion,
                    detallesCasas: result.data.detallesCasas
                });

                setSuccessDetails({
                    action: 'cuotas',
                    message: result.data.message,
                    casasActualizadas: result.data.casasActualizadas,
                    detallesCasas: result.data.detallesCasas
                });
                setShowSuccessModal(true);
            } else {
                console.log('🔵 [CORTE] Error en respuesta:', result.data.message);
                setChargeResult({
                    success: false,
                    message: result.data.message
                });
                alert(`⚠️ ${result.data.message}`);
            }
        } catch (error) {
            console.error('🔵 [CORTE] ❌ Error al calcular cuotas:', error);
            const errorMsg = error.message || 'Error desconocido al procesar la solicitud';
            setChargeResult({
                success: false,
                message: errorMsg
            });
            setErrorMessage(errorMsg);
            setShowErrorModal(true);
        } finally {
            setCalculatingCharges(false);
        }
    };

    // Handler para aplicar solo sanciones/intereses
    const handleAplicarSanciones = () => {
        if (!fraccionamientoId) {
            setErrorMessage('No se encontró el fraccionamiento');
            setShowErrorModal(true);
            return;
        }

        if (!configQuick.interesesEnabled) {
            setErrorMessage('Los intereses moratorios no están habilitados en la configuración financiera');
            setShowErrorModal(true);
            return;
        }

        setConfirmAction('sanciones');
        setShowConfirmModal(true);
    };

    const handleConfirmAplicarSanciones = async () => {
        console.log('🔴 [SANCIONES] Iniciando aplicación de sanciones (solo intereses)');
        console.log('🔴 [SANCIONES] fraccionamientoId:', fraccionamientoId);
        console.log('🔴 [SANCIONES] configQuick.interesesEnabled:', configQuick.interesesEnabled);
        console.log('🔴 [SANCIONES] Parámetros enviados:', {
            fraccionamientoId: fraccionamientoId,
            forzarAplicacion: true,
            fase: 'sanciones'
        });

        setShowConfirmModal(false);

        setCalculatingCharges(true);
        setChargeResult(null);

        try {
            if (currentUser && currentUser.getIdToken) {
                await currentUser.getIdToken(true);
            }

            const aplicarCargosManual = httpsCallable(functions, 'aplicarCargosMensualesManual');

            const params = {
                fraccionamientoId: fraccionamientoId,
                forzarAplicacion: true,
                fase: 'sanciones'
            };
            console.log('🔴 [SANCIONES] Invocando función con params:', params);

            const result = await aplicarCargosManual(params);
            console.log('🔴 [SANCIONES] Resultado recibido:', result.data);

            console.log('🔴 [SANCIONES] Success:', result.data.success);
            console.log('🔴 [SANCIONES] Casas actualizadas:', result.data.casasActualizadas);
            if (result.data.detallesCasas) {
                console.log('🔴 [SANCIONES] Detalles de casas:', result.data.detallesCasas);
            }

            if (result.data.success) {
                setChargeResult({
                    success: true,
                    message: result.data.message,
                    casasActualizadas: result.data.casasActualizadas,
                    configuracion: result.data.configuracion,
                    detallesCasas: result.data.detallesCasas
                });

                setSuccessDetails({
                    action: 'sanciones',
                    message: result.data.message,
                    casasActualizadas: result.data.casasActualizadas,
                    detallesCasas: result.data.detallesCasas
                });
                setShowSuccessModal(true);
            } else {
                console.log('🔴 [SANCIONES] Error en respuesta:', result.data.message);
                setChargeResult({
                    success: false,
                    message: result.data.message
                });
                alert(`⚠️ ${result.data.message}`);
            }
        } catch (error) {
            console.error('🔴 [SANCIONES] ❌ Error al aplicar sanciones:', error);
            const errorMsg = error.message || 'Error desconocido al procesar la solicitud';
            setChargeResult({
                success: false,
                message: errorMsg
            });
            setErrorMessage(errorMsg);
            setShowErrorModal(true);
        } finally {
            setCalculatingCharges(false);
        }
    };

    // Handler para marcar historial de pagos (fotografía de saldo actual y bandera sin alterar saldos)
    const handleMarcarHistorialPagos = () => {
        if (!fraccionamientoId) {
            setErrorMessage('No se encontró el fraccionamiento');
            setShowErrorModal(true);
            return;
        }

        setSelectedMonthHistorial(new Date().getMonth() + 1);
        setSelectedYearHistorial(new Date().getFullYear());
        setConfirmAction('marcar_historial');
        setShowConfirmModal(true);
    };

    const handleConfirmMarcarHistorialPagos = async () => {
        console.log('📌 [HISTORIAL] Iniciando marcado de historial de pagos (saldo actual y bandera)');
        console.log('📌 [HISTORIAL] fraccionamientoId:', fraccionamientoId, 'mes:', selectedMonthHistorial, 'anio:', selectedYearHistorial);

        setShowConfirmModal(false);
        setCalculatingCharges(true);
        setChargeResult(null);

        try {
            if (currentUser && currentUser.getIdToken) {
                await currentUser.getIdToken(true);
            }

            const marcarHistorialFunc = httpsCallable(functions, 'marcarHistorialPagosManual');

            const params = {
                fraccionamientoId: fraccionamientoId,
                mes: selectedMonthHistorial,
                anio: selectedYearHistorial
            };
            console.log('📌 [HISTORIAL] Invocando función con params:', params);

            const result = await marcarHistorialFunc(params);
            console.log('📌 [HISTORIAL] Resultado recibido:', result.data);

            if (result.data && result.data.success) {
                setChargeResult({
                    success: true,
                    message: result.data.message,
                    casasActualizadas: result.data.casasActualizadas,
                    detallesCasas: result.data.detallesCasas
                });

                setSuccessDetails({
                    action: 'marcar_historial',
                    message: result.data.message,
                    casasActualizadas: result.data.casasActualizadas,
                    detallesCasas: result.data.detallesCasas
                });
                setShowSuccessModal(true);
            } else {
                const msg = result.data?.message || 'Error al marcar el historial de pagos';
                setChargeResult({
                    success: false,
                    message: msg
                });
                alert(`⚠️ ${msg}`);
            }
        } catch (error) {
            console.error('📌 [HISTORIAL] ❌ Error al marcar historial:', error);
            const errorMsg = error.message || 'Error desconocido al procesar la solicitud';
            setChargeResult({
                success: false,
                message: errorMsg
            });
            setErrorMessage(errorMsg);
            setShowErrorModal(true);
        } finally {
            setCalculatingCharges(false);
        }
    };

    // Handlers eliminados: reseteo de adeudos y asignación temporal de rol ya no se ofrecen desde la UI.
    // --- HOOKS DE EFECTO ---

    // useEffect para obtener todos los datos necesarios
    useEffect(() => {
        console.log('Finanzas: currentUser.uid=', currentUser?.uid, 'fraccionamientoId=', fraccionamientoId);

        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const unsubscribers = []; // Array para guardar las funciones de limpieza

        // Listener para Ingresos
        const ingresosRef = collection(db, "fraccionamientos", fraccionamientoId, "ingresos");
        const qIngresos = query(ingresosRef, orderBy('fecha', 'desc'));
        unsubscribers.push(onSnapshot(qIngresos, (snapshot) => {
            const list = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(doc => !doc.deleted);
            setIngresos(list);
        }, (err) => {
            console.error('Finanzas: error en listener ingresos:', err);
            setIngresos([]);
        }));

        // Listener para Egresos
        const egresosRef = collection(db, "fraccionamientos", fraccionamientoId, "egresos");
        const qEgresos = query(egresosRef, orderBy('fecha', 'desc'));
        unsubscribers.push(onSnapshot(qEgresos, (snapshot) => {
            const list = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(doc => !doc.deleted);
            setEgresos(list);
        }, (err) => {
            console.error('Finanzas: error en listener egresos:', err);
            setEgresos([]);
        }));

        // Listener para Casas
        const casasRef = collection(db, "fraccionamientos", fraccionamientoId, "casas");
        unsubscribers.push(onSnapshot(query(casasRef), (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCasas(list);
        }, (err) => {
            console.error('Finanzas: error en listener casas:', err);
            setCasas([]);
        }));

        setLoading(false);

        // Limpieza de todos los listeners al desmontar
        return () => unsubscribers.forEach(unsub => unsub());
    }, [fraccionamientoId, currentUser]);

    // useEffect para CALCULAR el resumen cuando los datos cambien
    useEffect(() => {
        const ahora = new Date();
        const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);

        const ingresosMesValidos = ingresos.filter(ing => {
            if (!ing.fecha || !ing.fecha.toDate || ing.fecha.toDate() < inicioMes) return false;
            if (ing.deleted === true || ing.eliminado === true || ing.activo === false) return false;
            const concepto = String(ing.concepto || ing.descripcion || '').toLowerCase();
            const tipo = String(ing.tipo || '').toLowerCase();
            const metodoPago = String(ing.metodoPago || '').toLowerCase();

            // Excluir cargos automáticos del sistema, marcadores de deuda y cuotas no cobradas
            if (metodoPago === 'registro_automatico' ||
                concepto.includes('adeudo') ||
                concepto.includes('sin adeudo') ||
                concepto.includes('saldo final de mes') ||
                concepto.includes('recargo por morosidad') ||
                ing.esMarcadoHistorial === true ||
                ing.esSoloSaldoFinal === true ||
                tipo === 'saldo') {
                return false;
            }

            const monto = Number(ing.montoNeto ?? ing.monto ?? 0);
            if (monto <= 0) return false;
            return true;
        });

        const totalIngresos = ingresosMesValidos
            .reduce((sum, ing) => sum + (Number(ing.montoNeto ?? ing.monto) || 0), 0);

        let totalBolsaCentavos = 0;
        const partidasCentavosDetalle = [];

        ingresosMesValidos.forEach(ing => {
            if (Array.isArray(ing.pagos) && ing.pagos.length > 0) {
                ing.pagos.forEach(partida => {
                    const conc = String(partida.concepto || '').trim();
                    const concLower = conc.toLowerCase();
                    const monto = Number(partida.montoNeto ?? partida.monto ?? 0);
                    if (monto > 0 && (!esPartidaContableResidente(partida) || concLower.includes('otro') || concLower.includes('centavo'))) {
                        totalBolsaCentavos += monto;
                        partidasCentavosDetalle.push({
                            id: ing.id,
                            folio: ing.folio || (ing.folioConsecutivo ? `${ing.folioSerie || 'R'}${String(ing.folioConsecutivo).padStart(6, '0')}` : 'S/F'),
                            casaNumero: ing.casaNumero || 'N/A',
                            fecha: ing.fecha?.toDate ? ing.fecha.toDate() : new Date(),
                            fechaPagoOriginal: ing.fechaPagoOriginal || partida.fechaPago || null,
                            concepto: conc,
                            detalle: partida.detalle || '',
                            monto: monto,
                            metodoPago: partida.metodoPago || ing.metodoPago || 'N/A',
                            referencia: partida.referencia || ing.referencia || 'N/A'
                        });
                    }
                });
            } else {
                const conc = String(ing.concepto || '').trim();
                const concLower = conc.toLowerCase();
                const monto = Number(ing.montoNeto ?? ing.monto ?? 0);
                if (monto > 0 && (!esPartidaContableResidente(ing) || concLower.includes('otro') || concLower.includes('centavo'))) {
                    totalBolsaCentavos += monto;
                    partidasCentavosDetalle.push({
                        id: ing.id,
                        folio: ing.folio || (ing.folioConsecutivo ? `${ing.folioSerie || 'R'}${String(ing.folioConsecutivo).padStart(6, '0')}` : 'S/F'),
                        casaNumero: ing.casaNumero || 'N/A',
                        fecha: ing.fecha?.toDate ? ing.fecha.toDate() : new Date(),
                        fechaPagoOriginal: ing.fechaPagoOriginal || null,
                        concepto: conc,
                        detalle: ing.detalle || '',
                        monto: monto,
                        metodoPago: ing.metodoPago || 'N/A',
                        referencia: ing.referencia || 'N/A'
                    });
                }
            }
        });

        const totalEgresos = egresos
            .filter(eg => eg.fecha && eg.fecha.toDate && eg.fecha.toDate() >= inicioMes)
            .reduce((sum, eg) => sum + (eg.montoNeto ?? eg.monto ?? 0), 0);

        // Filtrar casas excluyendo caseta (la caseta no forma parte de las cuentas por cobrar a residentes)
        const casasSinCaseta = casas.filter(casa => !esCasaCaseta(casa));

        // Sumar los adeudos totales acumulados de todas las casas de residentes (Cuentas por Cobrar)
        const totalPorCobrar = casasSinCaseta.reduce((sum, casa) => {
            const totalAdeudo = calcularAdeudoConInteres(casa);
            return totalAdeudo > 0 ? sum + totalAdeudo : sum;
        }, 0);

        // Sumar el saldo a favor de todos los residentes (sin caseta)
        const totalSaldoFavor = casasSinCaseta
            .reduce((sum, casa) => sum + Number(casa.saldoFavor || 0), 0);

        setSummary({
            ingresosMes: totalIngresos,
            gastosMes: totalEgresos,
            balance: totalIngresos - totalEgresos,
            porCobrar: totalPorCobrar,
            saldoFavorTotal: totalSaldoFavor,
            // Neto por cobrar: solo el residuo a cobrar si el saldo a favor
            // no cubre las cuentas por cobrar. En caso contrario, es 0.
            netPorCobrar: Math.max(totalPorCobrar - totalSaldoFavor, 0),
            bolsaCentavos: Math.round(totalBolsaCentavos * 100) / 100,
            partidasCentavos: partidasCentavosDetalle
        });
    }, [ingresos, egresos, casas]);

    // useEffect para escuchar cambios en el resumen financiero importado
    useEffect(() => {
        if (!fraccionamientoId) return;
        // Listener para resumen financiero importado
        const fraccDocRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const unsubResumen = onSnapshot(fraccDocRef, (snap) => {
            const data = snap.data() || {};
            setModulosHabilitados(data.modulosHabilitados || {});
            // Extraer configuración financiera para la vista rápida
            const cfg = data.configuracionFinanciera || {};
            const montoMensual = Number(cfg.montoMensual || cfg.cuotaMensual || 0);
            // Solo sumar los consumos que están activados (checkbox marcado)
            const consumoSubtotal =
                (cfg.consumoAgua ? Number(cfg.consumoAguaMonto || 0) : 0)
                + (cfg.consumoGas ? Number(cfg.consumoGasMonto || 0) : 0)
                + (cfg.consumoInternet ? Number(cfg.consumoInternetMonto || 0) : 0)
                + (cfg.consumoLuz ? Number(cfg.consumoLuzMonto || 0) : 0);
            const mesActual = new Date().getMonth();
            const cuotaExtrasArr = Array.isArray(cfg.cuotaExtras) ? cfg.cuotaExtras : [];
            
            // Filtrar cuotas que aplican este mes para un cálculo estimado real
            const cuotasAplicables = cuotaExtrasArr.filter(c => {
                const monto = Number(c.monto || 0);
                if (monto <= 0) return false;
                const frecuencia = c.frecuencia || 'monthly';
                const mesInicio = c.mesInicio !== undefined ? Number(c.mesInicio) : mesActual;
                let intervalo = 1;
                switch(frecuencia) {
                    case 'monthly': intervalo = 1; break;
                    case 'bimonthly': intervalo = 2; break;
                    case 'quarterly': intervalo = 3; break;
                    case 'semiannual': intervalo = 6; break;
                    case 'yearly': intervalo = 12; break;
                    default: intervalo = 1;
                }
                return ((mesActual - mesInicio + 12) % 12) % intervalo === 0;
            });

            const cuotaExtrasSum = cuotasAplicables.reduce((s, c) => s + Number(c.monto || 0), 0) + Number(cfg.cuotaExtraMonto || 0);
            const cuotaExtrasCount = cuotasAplicables.length + ((cfg.cuotaExtraMonto && Number(cfg.cuotaExtraMonto) > 0) ? 1 : 0);
            const reservaAmount = cfg.fondoReservaEnabled ? (montoMensual * (Number(cfg.fondoReservaPercent || 0) / 100)) : 0;
            const subtotalCfg = montoMensual + cuotaExtrasSum + consumoSubtotal;
            const totalEstimado = subtotalCfg + reservaAmount;

            const multasListRaw = Array.isArray(cfg.multasAdministrativasList)
                ? cfg.multasAdministrativasList
                : (Array.isArray(cfg.multasList) ? cfg.multasList : []);
            const multasList = multasListRaw.map((m, index) => ({
                descripcion: m.descripcion || `Multa ${index + 1}`,
                monto: Number(m.monto || 0),
            }));
            const restriccionServicios = Array.isArray(cfg.restriccionServicios) ? cfg.restriccionServicios : [];

            setConfigQuick({
                montoMensual,
                consumoSubtotal,
                cuotaExtrasCount,
                cuotaExtrasSum,
                totalEstimado,
                tipoCalculo: cfg.tipoCalculo || 'igual',
                corteDia: cfg.corteDia != null ? String(cfg.corteDia) : '',
                sancionesInicioDia: cfg.sancionesInicioDia != null ? String(cfg.sancionesInicioDia) : '',
                consumoTipo: cfg.consumoTipo || 'no',
                discountEnabled: Boolean(cfg.discountEnabled),
                discountPercent: Number(cfg.discountPercent || 0),
                discountDescription: cfg.discountDescription || '',
                interesesEnabled: Boolean(cfg.interesesEnabled),
                interesesType: cfg.interesesType || 'fixed',
                interesesValue: Number(cfg.interesesValue || 0),
                mesesPermitidosDeuda: Number(cfg.interesesMesesPermitidosDeuda || 1),
                multasEnabled: Boolean(cfg.multasEnabled),
                multasCount: multasList.length,
                multasList,
                restriccionServiciosEnabled: Boolean(cfg.restriccionServiciosEnabled),
                restriccionServicios,
                restriccionMeses: cfg.restriccionMeses != null ? String(cfg.restriccionMeses) : '',
                annualBudgetEnabled: Boolean(cfg.annualBudgetEnabled),
                annualBudget: Number(cfg.annualBudget || 0),
                maintenanceAllocation: cfg.maintenanceAllocation ?? '',
                reserveAllocation: cfg.reserveAllocation ?? '',
                fondoReservaEnabled: Boolean(cfg.fondoReservaEnabled),
                fondoReservaPercent: Number(cfg.fondoReservaPercent || 0),
                fundTargetPercent: Number(cfg.fundTargetPercent ?? 0),
                fundUsePolicy: cfg.fundUsePolicy || '',
            });
        });
        return () => unsubResumen();
    }, [fraccionamientoId]);

    // Sincronizar el resumen en Firestore cada vez que cambian los adeudos en memoria
    useEffect(() => {
        if (!fraccionamientoId) return;

        const syncResumen = async () => {
            try {
                const fraccDocRef = doc(db, 'fraccionamientos', fraccionamientoId);
                await updateDoc(fraccDocRef, {
                    resumenFinanciero: {
                        cuentasPorCobrar: summary.porCobrar,
                        lastSync: new Date().toISOString(),
                    },
                });
            } catch (error) {
                console.error('Finanzas: no se pudo sincronizar resumenFinanciero', error);
            }
        };

        syncResumen();
    }, [fraccionamientoId, summary.porCobrar]);

    useEffect(() => {
        console.log('🔍 [Depuración Botones de Prueba]:', {
            rol: userData?.rol?.toLowerCase(),
            emailUsuario: userData?.email,
            emailActual: currentUser?.email,
            modulosHabilitados,
            botonesPruebaAdmin: modulosHabilitados?.botones_prueba_admin,
            esSuperOEmail: (userData?.rol?.toLowerCase() === 'platformadmin' ||
                            currentUser?.email === 'andywongajwf1990@gmail.com' ||
                            userData?.email === 'andywongajwf1990@gmail.com'),
            esAdminConToggle: (['superadmin', 'admin', 'admin_fracc', 'administrador'].includes(userData?.rol?.toLowerCase()) &&
                               modulosHabilitados?.botones_prueba_admin === true)
        });
    }, [userData, currentUser, modulosHabilitados]);

    const tipoCalculoLabel = configQuick.tipoCalculo === 'indiviso' ? 'Por indiviso / m²' : 'Cuota fija (todos pagan lo mismo)';
    const corteLabel = configQuick.corteDia ? `Día ${configQuick.corteDia}` : 'No definido';
    const sancionLabel = configQuick.sancionesInicioDia ? `Día ${configQuick.sancionesInicioDia}` : 'No definido';
    const consumoLabel = configQuick.consumoTipo !== 'no'
        ? `Individualizado (${formatCurrency(configQuick.consumoSubtotal)})`
        : 'No aplica';
    const cuotaExtraLabel = configQuick.cuotaExtrasCount > 0
        ? `${configQuick.cuotaExtrasCount} • ${formatCurrency(configQuick.cuotaExtrasSum)}`
        : 'Sin cuotas extra';
    const interesesNeedsPercent = typeof configQuick.interesesType === 'string' && configQuick.interesesType.includes('percent');
    const interesesValueLabel = configQuick.interesesEnabled
        ? (interesesNeedsPercent ? formatPercent(configQuick.interesesValue) : formatCurrency(configQuick.interesesValue))
        : '—';
    const interestTypeLabel = humanizeInteresType(configQuick.interesesType);
    const discountDescriptionLabel = configQuick.discountEnabled
        ? [configQuick.discountDescription?.trim(), 'Pronto pago'].filter(Boolean).join(', ')
        : '—';
    const multasDescriptionSummary = configQuick.multasCount > 0
        ? configQuick.multasList.slice(0, 2).map((m) => m.descripcion).join(', ') + (configQuick.multasCount > 2 ? '…' : '')
        : '—';
    const restriccionLabelsMap = {
        portal: 'Portal web',
        app: 'App móvil',
        boom: 'Acceso vehicular',
        chat: 'Chat comunitario',
        amenidades: 'Amenidades / Reservas',
        pagos: 'Pagos / Finanzas',
        documentos: 'Documentos',
    };
    const restriccionServiciosLabel = (configQuick.restriccionServiciosEnabled && configQuick.restriccionServicios.length > 0)
        ? configQuick.restriccionServicios.map((key) => restriccionLabelsMap[key] || key).join(', ')
        : '—';
    const restriccionMesesLabel = configQuick.restriccionServiciosEnabled && configQuick.restriccionMeses !== ''
        ? `${configQuick.restriccionMeses} mes(es)`
        : '—';
    const fundUsePolicyPreview = configQuick.fundUsePolicy
        ? (configQuick.fundUsePolicy.length > 160 ? `${configQuick.fundUsePolicy.slice(0, 160)}…` : configQuick.fundUsePolicy)
        : '—';

    // Calcular tasa de cobro (casas al corriente / total casas, excluyendo caseta)
    const tasaCobro = useMemo(() => {
        if (casas.length === 0) return 0;

        // Filtrar casas excluyendo caseta
        const casasSinCaseta = casas.filter(casa => !esCasaCaseta(casa));

        if (casasSinCaseta.length === 0) return 0;

        // Contar casas al corriente (sin adeudo)
        const casasAlCorriente = casasSinCaseta.filter(casa => {
            const adeudo = Number(casa.adeudo ?? 0);
            const adeudoCapital = Number(casa.adeudoCapital ?? 0);
            const adeudoIntereses = Number(casa.adeudoIntereses ?? 0);
            const totalAdeudo = adeudo > 0 ? adeudo : (adeudoCapital + adeudoIntereses);
            return totalAdeudo <= 0;
        }).length;

        return Math.round((casasAlCorriente / casasSinCaseta.length) * 100);
    }, [casas]);

    // Calcular porcentaje de deudores sin convenio
    const porcentajeSinConvenio = useMemo(() => {
        if (casas.length === 0) return 0;

        // Filtrar casas excluyendo caseta
        const casasSinCaseta = casas.filter(casa => !esCasaCaseta(casa));

        // Contar deudores totales
        const deudores = casasSinCaseta.filter(casa => {
            const adeudo = Number(casa.adeudo ?? 0);
            const adeudoCapital = Number(casa.adeudoCapital ?? 0);
            const adeudoIntereses = Number(casa.adeudoIntereses ?? 0);
            const totalAdeudo = adeudo > 0 ? adeudo : (adeudoCapital + adeudoIntereses);
            return totalAdeudo > 0;
        });

        const totalDeudores = deudores.length;

        if (totalDeudores === 0) return 0;

        // Contar cuántos de esos deudores NO tienen convenio activo
        const sinConvenio = deudores.filter(casa => casa.convenioActivo !== true).length;

        return ((sinConvenio / totalDeudores) * 100).toFixed(1);
    }, [casas]);

    // Datos para mini gráficos (últimos 6 meses)
    const ingresosUltimosMeses = useMemo(() => {
        const meses = [];
        const ahora = new Date();
        for (let i = 5; i >= 0; i--) {
            const fecha = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
            const total = ingresos
                .filter(ing => {
                    if (!ing.fecha || !ing.fecha.toDate) return false;
                    const ingFecha = ing.fecha.toDate();
                    if (ingFecha.getMonth() !== fecha.getMonth() || ingFecha.getFullYear() !== fecha.getFullYear()) return false;
                    
                    const concepto = String(ing.concepto || ing.descripcion || '').toLowerCase();
                    const tipo = String(ing.tipo || '').toLowerCase();
                    const metodoPago = String(ing.metodoPago || '').toLowerCase();

                    // Excluir cargos automáticos del sistema, marcadores de deuda y cuotas no cobradas
                    if (metodoPago === 'registro_automatico' ||
                        concepto.includes('adeudo') ||
                        concepto.includes('sin adeudo') ||
                        concepto.includes('saldo final de mes') ||
                        concepto.includes('recargo por morosidad') ||
                        ing.esMarcadoHistorial === true ||
                        ing.esSoloSaldoFinal === true ||
                        tipo === 'saldo') {
                        return false;
                    }

                    const monto = Number(ing.montoNeto ?? ing.monto ?? 0);
                    if (monto <= 0) return false;
                    return true;
                })
                .reduce((sum, ing) => sum + (Number(ing.montoNeto ?? ing.monto) || 0), 0);
            meses.push(total);
        }
        return meses;
    }, [ingresos]);

    const gastosUltimosMeses = useMemo(() => {
        const meses = [];
        const ahora = new Date();
        for (let i = 5; i >= 0; i--) {
            const fecha = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
            const total = egresos
                .filter(eg => {
                    if (!eg.fecha || !eg.fecha.toDate) return false;
                    const egFecha = eg.fecha.toDate();
                    return egFecha.getMonth() === fecha.getMonth() && egFecha.getFullYear() === fecha.getFullYear();
                })
                .reduce((sum, eg) => sum + (eg.montoNeto ?? eg.monto ?? 0), 0);
            meses.push(total);
        }
        return meses;
    }, [egresos]);

    const maxIngreso = Math.max(...ingresosUltimosMeses, 1);
    const maxGasto = Math.max(...gastosUltimosMeses, 1);

    // --- JSX DE RETORNO ---
    return (
        <div className="finanzas-page">
            <header className="finanzas-header">
                <div className="finanzas-header-content">
                    <div className="finanzas-header-icon">
                        <FaCreditCard />
                    </div>
                    <div className="finanzas-header-text">
                        <h1>Finanzas</h1>
                        <p>Gestión financiera del condominio</p>
                    </div>
                </div>
            </header>

            {/* Métricas destacadas */}
            <div className="finanzas-metrics-grid">
                <div className="finanzas-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#d1fae5' }}>
                        <FaArrowUp style={{ color: '#10b981' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{formatCurrency(summary.ingresosMes)}</div>
                        <div className="metric-label">Ingresos del Mes</div>
                        <div className="metric-mini-chart">
                            {ingresosUltimosMeses.map((valor, idx) => (
                                <div key={idx} className="mini-bar" style={{ height: `${(valor / maxIngreso) * 100}%` }}></div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="finanzas-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#fee2e2' }}>
                        <FaArrowDown style={{ color: '#ef4444' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{formatCurrency(summary.gastosMes)}</div>
                        <div className="metric-label">Gastos del Mes</div>
                        <div className="metric-mini-chart">
                            {gastosUltimosMeses.map((valor, idx) => (
                                <div key={idx} className="mini-bar negative" style={{ height: `${(valor / maxGasto) * 100}%` }}></div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="finanzas-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: summary.balance >= 0 ? '#dbeafe' : '#fee2e2' }}>
                        <FaDollarSign style={{ color: summary.balance >= 0 ? '#3b82f6' : '#ef4444' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value" style={{ color: summary.balance >= 0 ? '#10b981' : '#ef4444' }}>
                            {formatCurrency(summary.balance)}
                        </div>
                        <div className="metric-label">Balance Neto</div>
                        <div className="metric-badge" style={{ backgroundColor: summary.balance >= 0 ? '#d1fae5' : '#fee2e2', color: summary.balance >= 0 ? '#059669' : '#dc2626' }}>
                            {summary.balance >= 0 ? 'Positivo' : 'Negativo'}
                        </div>
                    </div>
                </div>

                <div className="finanzas-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#fef3c7' }}>
                        <FaWallet style={{ color: '#f59e0b' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{formatCurrency(summary.porCobrar)}</div>
                        <div className="metric-label">Cuentas por Cobrar</div>
                        <div className="metric-badge" style={{ backgroundColor: summary.porCobrar > 0 ? '#fef3c7' : '#d1fae5', color: summary.porCobrar > 0 ? '#d97706' : '#059669' }}>
                            {summary.porCobrar > 0 ? 'Pendiente' : 'Al día'}
                        </div>
                    </div>
                </div>

                <div className="finanzas-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#e9d5ff' }}>
                        <FaChartBar style={{ color: '#8b5cf6' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{tasaCobro}%</div>
                        <div className="metric-label">Tasa de Cobro</div>
                        <div className="metric-progress">
                            <div className="metric-progress-bar" style={{ width: `${tasaCobro}%` }}></div>
                        </div>

                        {/* Píldora interactiva para abrir desglose de Bolsa de Centavos */}
                        <button
                            type="button"
                            onClick={() => setIsBolsaCentavosModalOpen(true)}
                            title="Ver desglose de la Bolsa de Centavos y Tesorería"
                            style={{
                                marginTop: '10px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#f5f3ff',
                                border: '1px solid #ddd6fe',
                                borderRadius: '20px',
                                padding: '4px 10px',
                                cursor: 'pointer',
                                fontSize: '0.76rem',
                                color: '#6d28d9',
                                fontWeight: '600',
                                width: 'fit-content',
                                transition: 'all 0.2s',
                                boxShadow: '0 1px 2px rgba(109, 40, 217, 0.05)'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#ede9fe'; e.currentTarget.style.borderColor = '#c4b5fd'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#f5f3ff'; e.currentTarget.style.borderColor = '#ddd6fe'; }}
                        >
                            <FaCoins style={{ color: '#d97706', fontSize: '0.85rem' }} />
                            <span>Bolsa de Centavos:</span>
                            <strong style={{ color: '#5b21b6', fontSize: '0.82rem' }}>{formatCurrency(summary.bolsaCentavos || 0)}</strong>
                            <span style={{ fontSize: '0.7rem', color: '#7c3aed', marginLeft: '2px' }}>▸</span>
                        </button>
                    </div>
                </div>

                <div className="finanzas-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#fef3c7' }}>
                        <FaHandshake style={{ color: '#f59e0b' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{porcentajeSinConvenio}%</div>
                        <div className="metric-label">Deudores sin convenio</div>
                        <div className="metric-badge" style={{ backgroundColor: '#fffbeb', color: '#b45309' }}>
                            {porcentajeSinConvenio > 50 ? 'Atención' : 'Controlado'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Nota informativa para móvil - Solo lectura */}
            <div className="mobile-web-notice">
                <div className="mobile-web-notice-icon">🌐</div>
                <div className="mobile-web-notice-content">
                    <h4>Funciones Completas en Web</h4>
                    <p>
                        Por seguridad, las funciones de <strong>edición, configuración y gestión financiera</strong> solo están disponibles en la versión web del sistema.
                    </p>
                    <p style={{ marginTop: '8px', fontSize: '0.8rem', color: '#60a5fa' }}>
                        💡 Accede desde tu computadora para gestionar ingresos, egresos, configuración y reportes.
                    </p>
                </div>
            </div>

            <div className="finanzas-grid">
                {/* Configuración Financiera */}
                <div className="finanzas-card card-config">
                    <div className="finanzas-card-header" style={{ backgroundColor: '#8b5cf6' }}>
                        <div className="finanzas-card-header-content">
                            <div className="finanzas-card-icon">
                                <FaCalculator />
                            </div>
                            <div className="finanzas-card-title-wrapper">
                                <h3>Configuración Financiera</h3>
                                <p>Vista rápida de la configuración actual</p>
                            </div>
                            <div className="finanzas-card-actions">
                                <button
                                    type="button"
                                    className="btn-toggle-config"
                                    onClick={() => setShowConfigDetails((prev) => !prev)}
                                >
                                    {showConfigDetails ? 'Ocultar' : 'Ver detalle'}
                                </button>
                                <Link to="/configuraciones/financiera" className="btn-edit-config">Editar</Link>
                            </div>
                        </div>
                    </div>
                    <div className="finanzas-card-body">
                        <div className="config-summary-values">
                            <div className="config-value-item">
                                <small>Cuota mensual</small>
                                <div className="config-value">{formatCurrency(configQuick.montoMensual)}</div>
                            </div>
                            <div className="config-value-item">
                                <small>Consumos</small>
                                <div className="config-value">{formatCurrency(configQuick.consumoSubtotal)}</div>
                            </div>
                            <div className="config-value-item">
                                <small>Cuotas extra</small>
                                <div className="config-value">{configQuick.cuotaExtrasCount} • {formatCurrency(configQuick.cuotaExtrasSum)}</div>
                            </div>
                            <div className="config-value-item">
                                <small>Total estimado</small>
                                <div className="config-value strong">{formatCurrency(configQuick.totalEstimado)}</div>
                            </div>
                        </div>
                        <div className="config-badges-row">
                            <span className={`config-status-badge ${configQuick.discountEnabled ? 'active' : 'inactive'}`}>
                                Pronto pago {configQuick.discountEnabled ? formatPercent(configQuick.discountPercent) : 'off'}
                            </span>
                            <span className={`config-status-badge ${configQuick.interesesEnabled ? 'active' : 'inactive'}`}>
                                Intereses {configQuick.interesesEnabled ? interestTypeLabel : 'off'}
                            </span>
                            <span className={`config-status-badge ${configQuick.multasEnabled ? 'active' : 'inactive'}`}>
                                Multas {configQuick.multasEnabled ? configQuick.multasCount : 'off'}
                            </span>
                            <span className={`config-status-badge ${configQuick.fondoReservaEnabled ? 'active' : 'inactive'}`}>
                                Fondo {configQuick.fondoReservaEnabled ? formatPercent(configQuick.fondoReservaPercent) : 'off'}
                            </span>
                        </div>
                        {showConfigDetails && (
                            <div className="config-details-sections">
                                <div className="config-details-section">
                                    <h4>Cuotas base</h4>
                                    <ul>
                                        <li><strong>Tipo de cálculo</strong><span>{tipoCalculoLabel}</span></li>
                                        <li><strong>Día de corte</strong><span>{corteLabel}</span></li>
                                        <li><strong>Inicio sanciones</strong><span>{sancionLabel}</span></li>
                                        <li><strong>Consumo</strong><span>{consumoLabel}</span></li>
                                        <li><strong>Cuotas extra</strong><span>{cuotaExtraLabel}</span></li>
                                    </ul>
                                </div>
                                <div className="config-details-section">
                                    <h4>Descuentos y sanciones</h4>
                                    <ul>
                                        <li><strong>Descripción descuento</strong><span>{discountDescriptionLabel}</span></li>
                                        <li><strong>Valor intereses</strong><span>{interesesValueLabel}</span></li>
                                        <li><strong>Meses permitidos de mora</strong><span className="config-badge-meses">{configQuick.mesesPermitidosDeuda}</span></li>
                                        <li><strong>Multas registradas</strong><span>{configQuick.multasEnabled ? multasDescriptionSummary : '—'}</span></li>
                                    </ul>
                                </div>
                                <div className="config-details-section">
                                    <h4>Restricciones de servicios</h4>
                                    <div className={`config-status-badge ${configQuick.restriccionServiciosEnabled ? 'active' : 'inactive'}`}>
                                        {configQuick.restriccionServiciosEnabled ? 'Restricción activa' : 'Sin restricción'}
                                    </div>
                                    <ul>
                                        <li><strong>Meses de deuda</strong><span>{restriccionMesesLabel}</span></li>
                                        <li><strong>Servicios afectados</strong><span>{restriccionServiciosLabel}</span></li>
                                    </ul>
                                </div>
                                {(configQuick.annualBudgetEnabled || configQuick.fondoReservaEnabled) && (
                                    <div className="config-details-section">
                                        <h4>Presupuesto y fondo</h4>
                                        <ul>
                                            <li><strong>Presupuesto anual</strong><span>{configQuick.annualBudgetEnabled ? formatCurrency(configQuick.annualBudget) : '—'}</span></li>
                                            <li><strong>Mantenimiento</strong><span>{configQuick.annualBudgetEnabled ? formatCurrencyOrText(configQuick.maintenanceAllocation) : '—'}</span></li>
                                            <li><strong>Asignación fondo</strong><span>{configQuick.annualBudgetEnabled ? formatCurrencyOrText(configQuick.reserveAllocation) : '—'}</span></li>
                                            <li><strong>Porcentaje fondo</strong><span>{configQuick.annualBudgetEnabled ? formatPercent(configQuick.fondoReservaPercent) : '—'}</span></li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Botones de prueba para cálculos manuales - ACTIVADOS PARA ADMINISTRADORES */}
                        {((userData?.rol?.toLowerCase() === 'platformadmin' ||
                           currentUser?.email === 'andywongajwf1990@gmail.com' ||
                           userData?.email === 'andywongajwf1990@gmail.com') ||
                          ((['superadmin', 'admin', 'admin_fracc', 'administrador'].includes(userData?.rol?.toLowerCase())) &&
                           modulosHabilitados?.botones_prueba_admin === true)) && (
                            <div style={{
                                marginTop: '20px',
                                padding: '16px',
                                backgroundColor: '#fff3cd',
                                borderRadius: '8px',
                                border: '1px solid #ffc107',
                                marginBottom: '16px'
                            }}>
                                <p style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '13px',
                                    color: '#856404',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <FaExclamationTriangle style={{ fontSize: '14px' }} />
                                    <span>Botones de prueba: Usa estos botones para realizar cálculos manuales y pruebas del sistema.</span>
                                </p>
                                <div className="config-actions-footer" style={{
                                    display: 'flex',
                                    gap: '12px'
                                }}>
                                    <button
                                        className="btn-apply-charges-small"
                                        onClick={handleCalcularCuotasMensuales}
                                        disabled={calculatingCharges || configQuick.montoMensual <= 0}
                                        title="Aplicar cargos mensuales manualmente (para pruebas)"
                                        style={{
                                            padding: '10px 16px',
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: calculatingCharges || configQuick.montoMensual <= 0 ? 'not-allowed' : 'pointer',
                                            opacity: calculatingCharges || configQuick.montoMensual <= 0 ? 0.6 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <FaCalculator style={{ fontSize: '0.875rem' }} />
                                        {calculatingCharges ? 'Calculando...' : 'Aplicar Cargos'}
                                    </button>
                                    {configQuick.interesesEnabled && (
                                        <button
                                            className="btn-apply-charges-small btn-apply-sanctions"
                                            onClick={handleAplicarSanciones}
                                            disabled={calculatingCharges}
                                            title="Aplicar sanciones (intereses) manualmente (para pruebas)"
                                            style={{
                                                padding: '10px 16px',
                                                backgroundColor: '#dc2626',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: calculatingCharges ? 'not-allowed' : 'pointer',
                                                opacity: calculatingCharges ? 0.6 : 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <FaExclamationTriangle style={{ fontSize: '0.875rem' }} />
                                            {calculatingCharges ? 'Aplicando...' : 'Aplicar Sanciones'}
                                        </button>
                                    )}
                                    <button
                                        className="btn-apply-charges-small btn-mark-history"
                                        onClick={handleMarcarHistorialPagos}
                                        disabled={calculatingCharges}
                                        title="Marcar el saldo actual y bandera en el historial de pagos de todos los residentes"
                                        style={{
                                            padding: '10px 16px',
                                            backgroundColor: '#059669',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: calculatingCharges ? 'not-allowed' : 'pointer',
                                            opacity: calculatingCharges ? 0.6 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <FaBookmark style={{ fontSize: '0.875rem' }} />
                                        {calculatingCharges ? 'Marcando...' : 'Marcar Historial de Pagos'}
                                    </button>
                                </div>
                            </div>
                        )}
                        {chargeResult && (
                            <div className={`charge-result ${chargeResult.success ? 'success' : 'error'}`}>
                                {chargeResult.message}
                                {chargeResult.success && chargeResult.casasActualizadas > 0 && (
                                    <div className="charge-details">
                                        <small>✓ {chargeResult.casasActualizadas} casas actualizadas</small>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {/* Estado Financiero Detallado */}
                <div className="finanzas-card card-summary">
                    <div className="finanzas-card-header" style={{ backgroundColor: '#10b981' }}>
                        <div className="finanzas-card-header-content">
                            <div className="finanzas-card-icon">
                                <FaDollarSign />
                            </div>
                            <div className="finanzas-card-title-wrapper">
                                <h3>Estado Financiero</h3>
                                <p>Resumen financiero del mes actual</p>
                            </div>
                        </div>
                    </div>
                    <div className="finanzas-card-body">
                        {loading ? (
                            <p>Calculando...</p>
                        ) : (
                            <div className="summary-detailed">
                                <div className="summary-row">
                                    <div className="summary-label">Ingresos del mes:</div>
                                    <div className="summary-amount positive">{formatCurrency(summary.ingresosMes)}</div>
                                </div>
                                <div className="summary-row">
                                    <div className="summary-label">Gastos del mes:</div>
                                    <div className="summary-amount negative">{formatCurrency(summary.gastosMes)}</div>
                                </div>
                                <div className="summary-row highlight">
                                    <div className="summary-label">Balance:</div>
                                    <div className={`summary-amount ${summary.balance >= 0 ? 'positive' : 'negative'}`}>
                                        {formatCurrency(summary.balance)}
                                    </div>
                                </div>
                                <div className="summary-divider"></div>
                                <div className="summary-pair-grid">
                                    <div className="summary-pair-item">
                                        <div className="summary-label">Cuentas por cobrar:</div>
                                        <div className="summary-amount warning">{formatCurrency(summary.porCobrar)}</div>
                                        <div className="summary-badge" style={{ backgroundColor: summary.porCobrar > 0 ? '#fef3c7' : '#d1fae5', color: summary.porCobrar > 0 ? '#d97706' : '#059669' }}>
                                            {summary.porCobrar > 0 ? 'Pendiente' : 'Al día'}
                                        </div>
                                    </div>
                                    <div className="summary-pair-item">
                                        <div className="summary-label">Saldo a favor:</div>
                                        <div className="summary-amount positive">{formatCurrency(summary.saldoFavorTotal)}</div>
                                        <div className="summary-badge" style={{ backgroundColor: '#d1fae5', color: '#059669' }}>
                                            Disponible
                                        </div>
                                    </div>
                                </div>
                                <div className="summary-row final">
                                    <div className="summary-label">{summary.netPorCobrar > 0 ? 'Por cobrar neto:' : 'Saldo a Favor:'}</div>
                                    <div className={`summary-amount ${summary.netPorCobrar > 0 ? 'warning' : 'positive'}`}>
                                        {summary.netPorCobrar > 0
                                            ? formatCurrency(Math.abs(summary.netPorCobrar))
                                            : formatCurrency(Math.max(summary.saldoFavorTotal - summary.porCobrar, 0))}
                                    </div>
                                </div>
                                <div className="summary-subtext">
                                    Detalle: Cuentas por cobrar {formatCurrency(summary.porCobrar)} · Saldo a favor {formatCurrency(summary.saldoFavorTotal)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Reportes */}
                <div className="finanzas-card card-reports">
                    <div className="finanzas-card-header" style={{ backgroundColor: '#3b82f6' }}>
                        <div className="finanzas-card-header-content">
                            <div className="finanzas-card-icon">
                                <FaChartBar />
                            </div>
                            <div className="finanzas-card-title-wrapper">
                                <h3>Reportes</h3>
                                <p>Genera y descarga reportes financieros</p>
                            </div>
                        </div>
                    </div>
                    <div className="finanzas-card-body">
                        <div className="reports-actions">
                            <button className="btn-report btn-report-primary" onClick={openReport}>
                                <FaChartBar />
                                <span>Generar Reporte Detallado</span>
                            </button>
                            <button className="btn-report btn-report-secondary" onClick={() => setShowReporteMensualModal(true)}>
                                <FaDownload />
                                <span>Descargar Ingresos/Egresos</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Enlaces a secciones */}
                <div className="finanzas-links-grid">
                    <Link to="/finanzas/ingresos" className="finanzas-link-card">
                        <div className="link-card-icon" style={{ backgroundColor: '#d1fae5' }}>
                            <FaArrowUp style={{ color: '#10b981' }} />
                        </div>
                        <div className="link-card-content">
                            <h4>Ver Ingresos</h4>
                            <p>Consulta el historial completo de ingresos</p>
                        </div>
                    </Link>

                    <Link to="/finanzas/egresos" className="finanzas-link-card">
                        <div className="link-card-icon" style={{ backgroundColor: '#fee2e2' }}>
                            <FaArrowDown style={{ color: '#ef4444' }} />
                        </div>
                        <div className="link-card-content">
                            <h4>Ver Egresos</h4>
                            <p>Revisa y registra todos los gastos</p>
                        </div>
                    </Link>

                    <Link to="/finanzas/recibos-eliminados" className="finanzas-link-card link-card-deleted">
                        <div className="link-card-icon" style={{ backgroundColor: '#fee2e2' }}>
                            <FaReceipt style={{ color: '#dc2626' }} />
                        </div>
                        <div className="link-card-content">
                            <h4>Recibos Eliminados</h4>
                            <p>Recupera o elimina permanentemente recibos</p>
                        </div>
                    </Link>
                </div>
            </div>

            {/* Modal Reporte Detallado (resultado) */}
            {isReportOpen && reporteDetallado && (
                <div className="reporte-modal-overlay" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        closeReport();
                    }
                }}>
                    <div className="reporte-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="reporte-modal-header">
                            <h2>{`Estado de Cuenta Mensual ${getMonthLabel(selectedMonthReport)}`}</h2>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                {reporteGuardado?.contenidoHtml && (
                                    <button
                                        className="reporte-export-btn"
                                        onClick={handleExportReporteGuardado}
                                        title="Imprimir la versión guardada del mes"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '8px 12px',
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                    >
                                        <FaDownload size={14} />
                                        Versión guardada
                                    </button>
                                )}
                                <button
                                    className="reporte-export-btn"
                                    onClick={() => handleExportReport()}
                                    title="Imprimir o exportar el reporte"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '8px 16px',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                                >
                                    <FaDownload size={16} />
                                    Imprimir/Exportar
                                </button>
                                <span style={{ fontSize: '12px', color: '#4b5563' }}>
                                    {cargandoReporteGuardado
                                        ? 'Cargando versión guardada...'
                                        : reporteGuardado?.guardadoEn
                                            ? `Guardado: ${formatTimestamp(reporteGuardado.guardadoEn)}`
                                            : 'Sin versión guardada aún'}
                                </span>
                                <button className="reporte-close-btn" onClick={closeReport}>
                                    <FaTimes />
                                </button>
                            </div>
                        </div>

                        <div className="reporte-modal-body">
                            <div className="reporte-tabs">
                                    <button
                                        className={`reporte-tab ${activeReportTab === 'ingresos-egresos' ? 'active' : ''}`}
                                        onClick={() => setActiveReportTab('ingresos-egresos')}
                                    >
                                        Ingresos vs Egresos
                                    </button>
                                    <button
                                        className={`reporte-tab ${activeReportTab === 'notas' ? 'active' : ''}`}
                                        onClick={() => setActiveReportTab('notas')}
                                    >
                                        Notas
                                    </button>
                                    <button
                                        className={`reporte-tab ${activeReportTab === 'fondo' ? 'active' : ''}`}
                                        onClick={() => setActiveReportTab('fondo')}
                                    >
                                        Fondo
                                    </button>
                                    <button
                                        className={`reporte-tab ${activeReportTab === 'multas' ? 'active' : ''}`}
                                        onClick={() => setActiveReportTab('multas')}
                                    >
                                        Sanciones Administrativas
                                    </button>
                                    <button
                                        className={`reporte-tab ${activeReportTab === 'convenios' ? 'active' : ''}`}
                                        onClick={() => setActiveReportTab('convenios')}
                                    >
                                        Convenios Vigentes
                                    </button>
                                    <button
                                        className={`reporte-tab ${activeReportTab === 'casas-adeudo' ? 'active' : ''}`}
                                        onClick={() => setActiveReportTab('casas-adeudo')}
                                    >
                                        Casas con Adeudo
                                    </button>
                                </div>

                                <div className="reporte-impresion-toolbar">
                                    <div className="reporte-impresion-title">
                                        <span>🖨️ Secciones adicionales a imprimir:</span>
                                    </div>
                                    <div className="reporte-impresion-checks">
                                        <label className="impresion-check-item">
                                            <input
                                                type="checkbox"
                                                checked={seccionesImpresion.notas}
                                                onChange={(e) => setSeccionesImpresion(prev => ({ ...prev, notas: e.target.checked }))}
                                            />
                                            <span>Notas</span>
                                        </label>
                                        <label className="impresion-check-item">
                                            <input
                                                type="checkbox"
                                                checked={seccionesImpresion.sanciones}
                                                onChange={(e) => setSeccionesImpresion(prev => ({ ...prev, sanciones: e.target.checked }))}
                                            />
                                            <span>Sanciones Administrativas</span>
                                        </label>
                                        <label className="impresion-check-item">
                                            <input
                                                type="checkbox"
                                                checked={seccionesImpresion.convenios}
                                                onChange={(e) => setSeccionesImpresion(prev => ({ ...prev, convenios: e.target.checked }))}
                                            />
                                            <span>Convenios</span>
                                        </label>
                                        <label className="impresion-check-item">
                                            <input
                                                type="checkbox"
                                                checked={seccionesImpresion.casasAdeudo}
                                                onChange={(e) => setSeccionesImpresion(prev => ({ ...prev, casasAdeudo: e.target.checked }))}
                                            />
                                            <span>Casas con adeudo</span>
                                        </label>
                                    </div>
                                </div>

                            {(() => {
                                // Cálculos compartidos entre pestañas
                                const egresosFijos = (reporteDetallado.egresos || []).filter((e) => {
                                    const tipo = (e.tipoGasto || e.tipo || '').toLowerCase();
                                    const cat = (e.categoria || '').toLowerCase();
                                    return tipo === 'fijo' || cat.includes('fijo');
                                });
                                const egresosVariables = (reporteDetallado.egresos || []).filter((e) => {
                                    const tipo = (e.tipoGasto || e.tipo || '').toLowerCase();
                                    const cat = (e.categoria || '').toLowerCase();
                                    return tipo === 'variable' || (tipo !== 'fijo' && !cat.includes('fijo'));
                                });
                                const totalFijos = egresosFijos.reduce((sum, e) => sum + (Number(e.monto) || 0), 0);
                                const totalVariables = egresosVariables.reduce((sum, e) => sum + (Number(e.monto) || 0), 0);
                                const totalEgresos = totalFijos + totalVariables;

                                const ingresosAgrupados = (reporteDetallado.ingresos || []).reduce((acc, ing) => {
                                    const metodoPago = String(ing.metodoPago || '').toLowerCase();
                                    const conceptoLower = String(ing.concepto || ing.descripcion || ing.categoria || '').toLowerCase();

                                    // Excluir cargos automáticos del sistema, conceptos de adeudo y cuotas no cobradas
                                    if (metodoPago === 'registro_automatico' ||
                                        conceptoLower.includes('adeudo') ||
                                        conceptoLower.includes('sin adeudo') ||
                                        conceptoLower.includes('saldo final de mes') ||
                                        conceptoLower.includes('recargo por morosidad') ||
                                        ing.esMarcadoHistorial === true ||
                                        ing.esSoloSaldoFinal === true) {
                                        return acc;
                                    }

                                    if (Array.isArray(ing.pagos) && ing.pagos.length > 0) {
                                        ing.pagos.forEach(partida => {
                                            const concPartida = (partida.concepto || 'Sin concepto').trim();
                                            const montoPartida = Number(partida.montoNeto ?? partida.monto ?? 0);
                                            if (montoPartida > 0) {
                                                acc[concPartida] = (acc[concPartida] || 0) + montoPartida;
                                            }
                                        });
                                    } else {
                                        const concepto = (ing.concepto || ing.descripcion || ing.categoria || 'Sin concepto').trim();
                                        const monto = Number(ing.montoNeto ?? ing.monto) || 0;
                                        if (monto > 0) {
                                            acc[concepto] = (acc[concepto] || 0) + monto;
                                        }
                                    }
                                    return acc;
                                }, {});
                                const ingresosAgrupadosArr = Object.entries(ingresosAgrupados)
                                    .map(([concepto, monto]) => ({ concepto, monto }))
                                    .sort((a, b) => b.monto - a.monto);
                                const totalIngresos = ingresosAgrupadosArr.reduce((sum, item) => sum + (item.monto || 0), 0);
                                const balance = totalIngresos - totalEgresos;
                                const balanceColor = balance >= 0 ? '#16a34a' : '#dc2626';

                                // Usar el saldo inicial del reporte guardado si existe (estático), sino usar el del input
                                let saldoInicialParaVisualizacion = Number(saldoInicial) || 0;
                                if (reporteGuardado && reporteGuardado.resumenMeta && reporteGuardado.resumenMeta.saldoInicial !== undefined) {
                                    // Si hay un reporte guardado, usar su saldo inicial (estático, no se modifica)
                                    saldoInicialParaVisualizacion = Number(reporteGuardado.resumenMeta.saldoInicial) || 0;
                                }
                                const saldoFinal = saldoInicialParaVisualizacion + totalIngresos - totalEgresos;
                                const saldoFinalColor = saldoFinal >= 0 ? '#16a34a' : '#dc2626';

                                return (
                                    <>
                                        {activeReportTab === 'ingresos-egresos' && (
                                            <section className="reporte-section">
                                                <div className="reporte-duo">
                                                    <div className="reporte-duo-card">
                                                        <div className="reporte-duo-header">
                                                            <h3>Ingresos del mes</h3>
                                                            <span className="amount positive">{formatCurrencyReport(totalIngresos)}</span>
                                                        </div>
                                                        <div className="reporte-table-wrapper">
                                                            <table className="reporte-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Concepto</th>
                                                                        <th className="text-right">Monto</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {ingresosAgrupadosArr.map((item, idx) => (
                                                                        <tr key={idx}>
                                                                            <td>{item.concepto}</td>
                                                                            <td className="text-right">{formatCurrencyReport(item.monto)}</td>
                                                                        </tr>
                                                                    ))}
                                                                    <tr className="reporte-total-row">
                                                                        <td style={{ fontWeight: 'bold', textDecoration: 'underline' }}>Total Ingresos</td>
                                                                        <td className="text-right" style={{ fontWeight: 'bold', textDecoration: 'underline' }}>{formatCurrencyReport(totalIngresos)}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>

                                                    <div className="reporte-duo-card">
                                                        <div className="reporte-duo-header">
                                                            <h3>Egresos fijos</h3>
                                                            <span className="amount negative">{formatCurrencyReport(totalFijos)}</span>
                                                        </div>
                                                        <div className="reporte-table-wrapper">
                                                            <table className="reporte-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Descripción</th>
                                                                        <th className="text-right">Monto</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {egresosFijos.map((egr, idx) => (
                                                                        <tr key={idx}>
                                                                            <td>
                                                                                {egr.proveedor ? (
                                                                                    <>
                                                                                        <div><strong>{egr.proveedor}</strong></div>
                                                                                        <div style={{ fontSize: '0.85em', color: '#555' }}>
                                                                                            {egr.descripcion || egr.concepto || ''}
                                                                                        </div>
                                                                                    </>
                                                                                ) : (
                                                                                    egr.descripcion || egr.concepto || ''
                                                                                )}
                                                                            </td>
                                                                            <td className="text-right">{formatCurrencyReport(egr.monto)}</td>
                                                                        </tr>
                                                                    ))}
                                                                    <tr className="reporte-total-row">
                                                                        <td style={{ fontWeight: 'bold', textDecoration: 'underline' }}>Total Egresos Fijos</td>
                                                                        <td className="text-right" style={{ fontWeight: 'bold', textDecoration: 'underline' }}>{formatCurrencyReport(totalFijos)}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>

                                                    <div className="reporte-duo-card">
                                                        <div className="reporte-duo-header">
                                                            <h3>Egresos variables</h3>
                                                            <span className="amount negative">{formatCurrencyReport(totalVariables)}</span>
                                                        </div>
                                                        <div className="reporte-table-wrapper">
                                                            <table className="reporte-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Descripción</th>
                                                                        <th className="text-right">Monto</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {egresosVariables.map((egr, idx) => (
                                                                        <tr key={idx}>
                                                                            <td>
                                                                                {egr.proveedor ? (
                                                                                    <>
                                                                                        <div><strong>{egr.proveedor}</strong></div>
                                                                                        <div style={{ fontSize: '0.85em', color: '#555' }}>
                                                                                            {egr.descripcion || egr.concepto || ''}
                                                                                        </div>
                                                                                    </>
                                                                                ) : (
                                                                                    egr.descripcion || egr.concepto || ''
                                                                                )}
                                                                            </td>
                                                                            <td className="text-right">{formatCurrencyReport(egr.monto)}</td>
                                                                        </tr>
                                                                    ))}
                                                                    <tr className="reporte-total-row">
                                                                        <td style={{ fontWeight: 'bold', textDecoration: 'underline' }}>Total Egresos Variables</td>
                                                                        <td className="text-right" style={{ fontWeight: 'bold', textDecoration: 'underline' }}>{formatCurrencyReport(totalVariables)}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Total de Egresos + Resumen Finanzas */}
                                                <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
                                                    {/* Total de Egresos - 1/3 */}
                                                    <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                                                        <h3 style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>Total de Egresos</h3>
                                                        <table className="reporte-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <tbody>
                                                                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                                                    <td style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '500', textAlign: 'left' }}>Total de egresos (fijos y variables)</td>
                                                                    <td className="text-right" style={{ padding: '5px 3px', fontSize: '13px', fontWeight: 'bold', minWidth: '68px', color: '#dc2626' }}>{formatCurrencyReport(totalEgresos)}</td>
                                                                </tr>
                                                                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                                                    <td style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '500', textAlign: 'left' }}>Total de ingresos</td>
                                                                    <td className="text-right" style={{ padding: '5px 3px', fontSize: '13px', fontWeight: 'bold', minWidth: '68px', color: '#16a34a' }}>{formatCurrencyReport(totalIngresos)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '700', textDecoration: 'underline', textAlign: 'left' }}>Total (ingresos - egresos)</td>
                                                                    <td className="text-right" style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '700', textDecoration: 'underline', minWidth: '68px', color: balanceColor }}>
                                                                        {balance >= 0 ? formatCurrencyReport(balance) : `- ${formatCurrencyReport(Math.abs(balance))}`}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Resumen Finanzas - 2/3 */}
                                                    <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                                                        <h3 style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>Resumen Finanzas</h3>
                                                        <div style={{ marginBottom: '16px' }}>
                                                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                                                                Saldo Inicial de Fondo (banco/caja chica)
                                                            </label>
                                                            <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontStyle: 'italic', margin: '4px 0 8px 0' }}>
                                                                {reporteGuardado?.contenidoHtml
                                                                    ? 'Este reporte ya fue guardado. El saldo inicial mostrado es el que se usó cuando se generó el reporte (estático).'
                                                                    : 'Ingresa el saldo del fondo (banco o caja chica) del fraccionamiento para calcular el saldo final. Este se guardará para este mes específico.'}
                                                            </p>
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={saldoInicial}
                                                                    onChange={(e) => {
                                                                        // Solo permitir cambios si no hay reporte guardado
                                                                        if (!reporteGuardado?.contenidoHtml) {
                                                                            setSaldoInicial(e.target.value);
                                                                        }
                                                                    }}
                                                                    placeholder="Ingresa el saldo inicial"
                                                                    disabled={!!reporteGuardado?.contenidoHtml}
                                                                    style={{
                                                                        flex: 1,
                                                                        padding: '6px 10px',
                                                                        fontSize: '13px',
                                                                        border: '1px solid #ccc',
                                                                        borderRadius: '6px',
                                                                        boxSizing: 'border-box',
                                                                        backgroundColor: reporteGuardado?.contenidoHtml ? '#f3f4f6' : 'white',
                                                                        cursor: reporteGuardado?.contenidoHtml ? 'not-allowed' : 'text'
                                                                    }}
                                                                />
                                                                <button
                                                                    onClick={handleGuardarSaldoInicial}
                                                                    disabled={!!reporteGuardado?.contenidoHtml}
                                                                    style={{
                                                                        padding: '6px 12px',
                                                                        fontSize: '13px',
                                                                        fontWeight: '600',
                                                                        backgroundColor: reporteGuardado?.contenidoHtml ? '#9ca3af' : '#0066cc',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '6px',
                                                                        cursor: reporteGuardado?.contenidoHtml ? 'not-allowed' : 'pointer',
                                                                        opacity: reporteGuardado?.contenidoHtml ? 0.6 : 1,
                                                                        transition: 'background-color 0.2s',
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        if (!reporteGuardado?.contenidoHtml) {
                                                                            e.target.style.backgroundColor = '#0052a3';
                                                                        }
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        if (!reporteGuardado?.contenidoHtml) {
                                                                            e.target.style.backgroundColor = '#0066cc';
                                                                        }
                                                                    }}
                                                                >
                                                                    Guardar
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <table className="reporte-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <tbody>
                                                                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                                                    <td style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '500', textAlign: 'left' }}>Saldo Inicial</td>
                                                                    <td className="text-right" style={{ padding: '5px 3px', fontSize: '13px', fontWeight: 'bold', minWidth: '68px', color: '#0066cc' }}>{formatCurrencyReport(saldoInicialParaVisualizacion)}</td>
                                                                </tr>
                                                                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                                                    <td style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '500', textAlign: 'left' }}>+ Ingresos</td>
                                                                    <td className="text-right" style={{ padding: '5px 3px', fontSize: '13px', fontWeight: 'bold', minWidth: '68px', color: '#16a34a' }}>{formatCurrencyReport(totalIngresos)}</td>
                                                                </tr>
                                                                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                                                    <td style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '500', textAlign: 'left' }}>- Egresos</td>
                                                                    <td className="text-right" style={{ padding: '5px 3px', fontSize: '13px', fontWeight: 'bold', minWidth: '68px', color: '#dc2626' }}>{formatCurrencyReport(totalEgresos)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '700', textDecoration: 'underline', textAlign: 'left' }}>Saldo Final de Fondo</td>
                                                                    <td className="text-right" style={{ padding: '5px 3px', fontSize: '13px', fontWeight: '700', textDecoration: 'underline', minWidth: '68px', color: saldoFinalColor }}>
                                                                        {saldoFinal >= 0 ? formatCurrencyReport(saldoFinal) : `- ${formatCurrencyReport(Math.abs(saldoFinal))}`}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </section>
                                        )}

                                        {activeReportTab === 'notas' && (
                                            <section className="reporte-section">
                                                <div style={{
                                                    maxWidth: '900px',
                                                    margin: '0 auto',
                                                    padding: '20px'
                                                }}>
                                                    <h3 style={{
                                                        fontSize: '20px',
                                                        fontWeight: '600',
                                                        marginBottom: '24px',
                                                        color: '#1f2937',
                                                        textAlign: 'center'
                                                    }}>
                                                        Notas del Reporte
                                                    </h3>

                                                    <div style={{
                                                        backgroundColor: '#fffbeb',
                                                        border: '2px solid #fcd34d',
                                                        borderRadius: '8px',
                                                        padding: '24px',
                                                        marginBottom: '24px'
                                                    }}>
                                                        <label style={{
                                                            display: 'block',
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            color: '#92400e',
                                                            marginBottom: '12px'
                                                        }}>
                                                            Agrega notas o comentarios relevantes sobre los ingresos y egresos mostrados en este reporte:
                                                        </label>
                                                        <textarea
                                                            value={notasReporte}
                                                            onChange={(e) => setNotasReporte(e.target.value)}
                                                            placeholder="Escribe aquí tus comentarios, observaciones o notas importantes..."
                                                            style={{
                                                                width: '100%',
                                                                minHeight: '300px',
                                                                padding: '16px',
                                                                fontSize: '14px',
                                                                border: '1px solid #fcd34d',
                                                                borderRadius: '6px',
                                                                outline: 'none',
                                                                fontFamily: 'inherit',
                                                                resize: 'vertical',
                                                                backgroundColor: 'white'
                                                            }}
                                                        />
                                                        <p style={{
                                                            fontSize: '12px',
                                                            color: '#92400e',
                                                            marginTop: '12px',
                                                            marginBottom: '0'
                                                        }}>
                                                            💡 Tip: Estas notas se incluirán en el reporte cuando lo imprimas o exportes.
                                                        </p>
                                                    </div>

                                                    <div style={{
                                                        backgroundColor: '#f0f9ff',
                                                        border: '1px solid #bfdbfe',
                                                        borderRadius: '8px',
                                                        padding: '16px',
                                                        marginBottom: '24px'
                                                    }}>
                                                        <p style={{
                                                            fontSize: '13px',
                                                            color: '#1e40af',
                                                            marginBottom: '0',
                                                            lineHeight: '1.5'
                                                        }}>
                                                            <strong>ℹ️ Información:</strong> Las notas se guardan temporalmente y se incluirán en el reporte descargado o impreso.
                                                            No se persisten en la base de datos a menos que guardes el estado de la cuenta manualmente.
                                                        </p>
                                                    </div>
                                                </div>
                                            </section>
                                        )}

                                        {activeReportTab === 'multas' && (
                                            <section className="reporte-section">
                                                <div style={{
                                                    maxWidth: '900px',
                                                    margin: '0 auto',
                                                    padding: '20px'
                                                }}>
                                                    <h3 style={{
                                                        fontSize: '20px',
                                                        fontWeight: '600',
                                                        marginBottom: '24px',
                                                        color: '#1f2937',
                                                        textAlign: 'center'
                                                    }}>
                                                        Sanciones Administrativas
                                                    </h3>

                                                    {multasConfig?.habilitarMultasAdministrativas ? (
                                                        <div>
                                                            <div style={{
                                                                backgroundColor: '#f0fdf4',
                                                                border: '2px solid #86efac',
                                                                borderRadius: '8px',
                                                                padding: '16px',
                                                                marginBottom: '24px'
                                                            }}>
                                                                <p style={{ fontSize: '14px', color: '#166534', marginBottom: '4px', fontWeight: '600' }}>
                                                                    ✅ Sistema de sanciones ACTIVADO
                                                                </p>
                                                                <p style={{ fontSize: '13px', color: '#166534', marginBottom: '0' }}>
                                                                    {multasConfig.multasAdministrativasList?.length || 0} sanciones configuradas en el sistema.
                                                                </p>
                                                            </div>

                                                            {/* Mostrar tabla de multas del sistema */}
                                                            {multasConfig.multasAdministrativasList && multasConfig.multasAdministrativasList.length > 0 ? (
                                                                <div style={{
                                                                    backgroundColor: 'white',
                                                                    border: '1px solid #e5e7eb',
                                                                    borderRadius: '8px',
                                                                    padding: '24px',
                                                                    marginBottom: '24px'
                                                                }}>
                                                                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
                                                                        Catálogo de Sanciones Configuradas
                                                                    </h4>
                                                                    <div style={{ overflowX: 'auto' }}>
                                                                        <table style={{
                                                                            width: '100%',
                                                                            borderCollapse: 'collapse',
                                                                            fontSize: '14px'
                                                                        }}>
                                                                            <thead>
                                                                                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Descripción</th>
                                                                                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Monto</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {multasConfig.multasAdministrativasList.map((multa, index) => (
                                                                                    <tr key={multa.id || index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                                        <td style={{ padding: '12px', color: '#374151' }}>{multa.descripcion || multa.description || 'Sin descripción'}</td>
                                                                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: '500', color: '#dc2626' }}>
                                                                                            ${parseFloat(multa.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px', marginBottom: '0' }}>
                                                                        💡 Estas sanciones se aplican automáticamente cuando se registra una infracción en el sistema.
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                <div style={{
                                                                    backgroundColor: '#fff7ed',
                                                                    border: '1px solid #fdba74',
                                                                    borderRadius: '8px',
                                                                    padding: '16px',
                                                                    marginBottom: '24px',
                                                                    textAlign: 'center'
                                                                }}>
                                                                    <p style={{ fontSize: '14px', color: '#92400e', marginBottom: '0' }}>
                                                                        ⚠️ El sistema está activado pero no hay sanciones configuradas. Ve a Configuración Financiera para agregar sanciones.
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div style={{
                                                            backgroundColor: '#fef3c7',
                                                            border: '2px solid #fbbf24',
                                                            borderRadius: '8px',
                                                            padding: '16px',
                                                            marginBottom: '24px'
                                                        }}>
                                                            <p style={{ fontSize: '14px', color: '#92400e', marginBottom: '8px', fontWeight: '600' }}>
                                                                ℹ️ Sistema de sanciones DESACTIVADO
                                                            </p>
                                                            <p style={{ fontSize: '13px', color: '#92400e', marginBottom: '0' }}>
                                                                Puedes agregar sanciones manualmente o activar el sistema en Configuración Financiera.
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div style={{
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        padding: '24px',
                                                        marginBottom: '24px'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            marginBottom: '16px'
                                                        }}>
                                                            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: 0 }}>
                                                                Registro de Sanciones
                                                            </h4>
                                                            <button
                                                                onClick={agregarMulta}
                                                                style={{
                                                                    backgroundColor: '#3b82f6',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    padding: '8px 16px',
                                                                    fontSize: '14px',
                                                                    fontWeight: '500',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                + Agregar Sanción
                                                            </button>
                                                        </div>

                                                        {multasManual.length === 0 ? (
                                                            <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', padding: '40px 0' }}>
                                                                No hay sanciones registradas. Haz clic en "+ Agregar Sanción" para comenzar.
                                                            </p>
                                                        ) : (
                                                            <div style={{ overflowX: 'auto' }}>
                                                                <table style={{
                                                                    width: '100%',
                                                                    borderCollapse: 'collapse',
                                                                    fontSize: '14px'
                                                                }}>
                                                                    <thead>
                                                                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Casa</th>
                                                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Infracción</th>
                                                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Fecha</th>
                                                                            <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Monto</th>
                                                                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Acciones</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {multasManual.map((multa) => (
                                                                            <tr key={multa.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                                <td style={{ padding: '12px' }}>
                                                                                    <input
                                                                                        type="text"
                                                                                        list={`casas-list-${multa.id}`}
                                                                                        value={multa.casa}
                                                                                        onChange={(e) => actualizarMulta(multa.id, 'casa', e.target.value)}
                                                                                        placeholder="Ej: 42"
                                                                                        style={{
                                                                                            width: '100%',
                                                                                            padding: '8px',
                                                                                            border: '1px solid #d1d5db',
                                                                                            borderRadius: '4px',
                                                                                            fontSize: '14px'
                                                                                        }}
                                                                                    />
                                                                                    <datalist id={`casas-list-${multa.id}`}>
                                                                                        {casasLista.map((casa) => (
                                                                                            <option key={casa.id} value={casa.numero} />
                                                                                        ))}
                                                                                    </datalist>
                                                                                </td>
                                                                                <td style={{ padding: '12px' }}>
                                                                                    <div style={{ position: 'relative' }}>
                                                                                        <input
                                                                                            type="text"
                                                                                            list={`multas-list-${multa.id}`}
                                                                                            value={multa.infraccion}
                                                                                            onChange={(e) => handleInfraccionInput(multa.id, e.target.value)}
                                                                                            onBlur={(e) => setTimeout(() => setSugerenciasMulta({ rowId: null, items: [] }), 150)}
                                                                                            placeholder="Descripción de la infracción"
                                                                                            style={{
                                                                                                width: '100%',
                                                                                                padding: '8px',
                                                                                                border: '1px solid #d1d5db',
                                                                                                borderRadius: '4px',
                                                                                                fontSize: '14px'
                                                                                            }}
                                                                                        />
                                                                                        <datalist id={`multas-list-${multa.id}`}>
                                                                                            {(multasConfig?.multasAdministrativasList || []).map((cat) => (
                                                                                                <option key={cat.id}
                                                                                                    value={cat.descripcion || `Multa ${cat.id}`}
                                                                                                    label={`$${(parseFloat(cat.monto) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                                                                />
                                                                                            ))}
                                                                                        </datalist>
                                                                                        {sugerenciasMulta.rowId === multa.id && sugerenciasMulta.items.length > 0 && (
                                                                                            <ul style={{ position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '200px', overflowY: 'auto', margin: '2px 0 0 0', padding: 0, listStyleType: 'none' }}>
                                                                                                {sugerenciasMulta.items.map((item) => (
                                                                                                    <li key={item.id}
                                                                                                        onMouseDown={(e) => { e.preventDefault(); actualizarMulta(multa.id, 'infraccion', item.descripcion || ''); actualizarMulta(multa.id, 'monto', parseFloat(item.monto) || 0); setSugerenciasMulta({ rowId: null, items: [] }); }}
                                                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                                                                                        style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6' }}
                                                                                                    >
                                                                                                        <span style={{ fontWeight: '500', color: '#111827' }}>{item.descripcion}</span>
                                                                                                        <span style={{ color: '#059669', fontWeight: '600' }}>${(parseFloat(item.monto) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                                                                    </li>
                                                                                                ))}
                                                                                            </ul>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                                <td style={{ padding: '12px' }}>
                                                                                    <input
                                                                                        type="date"
                                                                                        value={multa.fecha}
                                                                                        onChange={(e) => actualizarMulta(multa.id, 'fecha', e.target.value)}
                                                                                        style={{
                                                                                            width: '100%',
                                                                                            padding: '8px',
                                                                                            border: '1px solid #d1d5db',
                                                                                            borderRadius: '4px',
                                                                                            fontSize: '14px'
                                                                                        }}
                                                                                    />
                                                                                </td>
                                                                                <td style={{ padding: '12px' }}>
                                                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                        <span style={{ marginRight: '4px', color: '#6b7280' }}>$</span>
                                                                                        <input
                                                                                            type="number"
                                                                                            value={multa.monto}
                                                                                            onChange={(e) => actualizarMulta(multa.id, 'monto', e.target.value)}
                                                                                            placeholder="0.00"
                                                                                            step="0.01"
                                                                                            min="0"
                                                                                            style={{
                                                                                                width: '100%',
                                                                                                padding: '8px',
                                                                                                border: '1px solid #d1d5db',
                                                                                                borderRadius: '4px',
                                                                                                fontSize: '14px',
                                                                                                textAlign: 'right'
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </td>
                                                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                                                    <button
                                                                                        onClick={() => eliminarMulta(multa.id)}
                                                                                        style={{
                                                                                            backgroundColor: '#ef4444',
                                                                                            color: 'white',
                                                                                            border: 'none',
                                                                                            borderRadius: '4px',
                                                                                            padding: '6px 12px',
                                                                                            fontSize: '13px',
                                                                                            cursor: 'pointer'
                                                                                        }}
                                                                                    >
                                                                                        Eliminar
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                        <tr style={{ backgroundColor: '#f9fafb', fontWeight: '600' }}>
                                                                            <td colSpan="3" style={{ padding: '12px', textAlign: 'right', fontSize: '15px' }}>
                                                                                Total de Sanciones:
                                                                            </td>
                                                                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '15px', color: '#dc2626' }}>
                                                                                ${calcularTotalMultas().toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </td>
                                                                            <td></td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div style={{
                                                        backgroundColor: '#f0f9ff',
                                                        border: '1px solid #bfdbfe',
                                                        borderRadius: '8px',
                                                        padding: '16px'
                                                    }}>
                                                        <p style={{
                                                            fontSize: '13px',
                                                            color: '#1e40af',
                                                            marginBottom: '0',
                                                            lineHeight: '1.5'
                                                        }}>
                                                            <strong>💡 Tip:</strong> Las sanciones registradas aquí se incluirán en el reporte impreso.
                                                            Para que se registren automáticamente, activa la configuración de "Sanciones Administrativas" en el módulo de Configuración Financiera.
                                                        </p>
                                                    </div>
                                                </div>
                                            </section>
                                        )}

                                        {activeReportTab === 'convenios' && (
                                            <section className="reporte-section">
                                                <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
                                                    <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        🤝 Convenios Vigentes
                                                    </h3>

                                                    {conveniosVigentes.length === 0 ? (
                                                        <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', padding: '24px', textAlign: 'center' }}>
                                                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                                                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#065f46', marginBottom: '8px' }}>
                                                                No hay convenios vigentes
                                                            </p>
                                                            <p style={{ fontSize: '14px', color: '#047857', margin: 0 }}>
                                                                Actualmente no hay residentes con convenios de pago activos.
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                    <span style={{ fontSize: '24px' }}>🤝</span>
                                                                    <div>
                                                                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '4px' }}>
                                                                            {conveniosVigentes.length} {conveniosVigentes.length === 1 ? 'convenio activo' : 'convenios activos'}
                                                                        </p>
                                                                        <p style={{ fontSize: '13px', color: '#15803d', margin: 0 }}>
                                                                            Estos residentes están exentos de intereses moratorios durante el período de convenio.
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                                                    <thead>
                                                                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                                            <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '30%' }}>
                                                                                No. Casa
                                                                            </th>
                                                                            <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '40%' }}>
                                                                                Fecha Inicio
                                                                            </th>
                                                                            <th style={{ padding: '14px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '30%' }}>
                                                                                Estado
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {conveniosVigentes.map((convenio, index) => {
                                                                            const fechaInicio = convenio.fechaActivacion ?
                                                                                new Date(convenio.fechaActivacion).toLocaleDateString('es-MX', {
                                                                                    year: 'numeric',
                                                                                    month: 'short',
                                                                                    day: 'numeric'
                                                                                }) : 'N/A';
                                                                            const estado = convenio.fechaDesactivacion ? 'Finalizado' : 'Activo';

                                                                            return (
                                                                                <tr key={convenio.id || index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                                    <td style={{ padding: '14px', color: '#374151', fontWeight: '500' }}>
                                                                                        {convenio.numero}
                                                                                    </td>
                                                                                    <td style={{ padding: '14px', color: '#6b7280' }}>
                                                                                        {fechaInicio}
                                                                                    </td>
                                                                                    <td style={{ padding: '14px', textAlign: 'center' }}>
                                                                                        <span style={{
                                                                                            display: 'inline-block',
                                                                                            padding: '4px 12px',
                                                                                            borderRadius: '12px',
                                                                                            fontSize: '12px',
                                                                                            fontWeight: '600',
                                                                                            backgroundColor: estado === 'Activo' ? '#dcfce7' : '#fef3c7',
                                                                                            color: estado === 'Activo' ? '#166534' : '#92400e'
                                                                                        }}>
                                                                                            {estado}
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>

                                                            <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '16px', marginTop: '20px' }}>
                                                                <p style={{ fontSize: '13px', color: '#1e40af', marginBottom: '0', lineHeight: '1.5' }}>
                                                                    <strong>💡 Información:</strong> Los convenios vigentes eximen a los residentes de intereses moratorios durante el período de activación.
                                                                    Esta información se incluirá en el reporte impreso.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </section>
                                        )}

                                        {activeReportTab === 'casas-adeudo' && (
                                            <section className="reporte-section">
                                                <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
                                                    <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        🏠 Casas con Adeudo
                                                    </h3>

                                                    {casasConAdeudo.length === 0 ? (
                                                        <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', padding: '24px', textAlign: 'center' }}>
                                                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                                                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#065f46', marginBottom: '8px' }}>
                                                                ¡Excelente! No hay casas con adeudos pendientes
                                                            </p>
                                                            <p style={{ fontSize: '14px', color: '#047857', margin: 0 }}>
                                                                Todas las viviendas están al corriente con sus pagos.
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                    <span style={{ fontSize: '24px' }}>⚠️</span>
                                                                    <div>
                                                                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                                                                            {casasConAdeudo.length} {casasConAdeudo.length === 1 ? 'casa tiene' : 'casas tienen'} adeudos pendientes
                                                                        </p>
                                                                        <p style={{ fontSize: '13px', color: '#b45309', margin: 0 }}>
                                                                            Total adeudado: ${casasConAdeudo.reduce((sum, c) => sum + (parseFloat(c.adeudo) || 0), 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                                                    <thead>
                                                                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                                            <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '30%' }}>
                                                                                No. Casa
                                                                            </th>
                                                                            <th style={{ padding: '14px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>
                                                                                Adeudo
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {casasConAdeudo.map((casa, index) => (
                                                                            <tr key={casa.id} style={{ borderBottom: index < casasConAdeudo.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                                                                <td style={{ padding: '14px', color: '#111827', fontWeight: '500' }}>
                                                                                    Casa {casa.numero}
                                                                                </td>
                                                                                <td style={{ padding: '14px', textAlign: 'right', color: '#dc2626', fontWeight: '600' }}>
                                                                                    ${parseFloat(casa.adeudo).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                        <tr style={{ backgroundColor: '#fee2e2', borderTop: '2px solid #fca5a5' }}>
                                                                            <td style={{ padding: '16px', fontWeight: '700', fontSize: '15px', color: '#991b1b' }}>
                                                                                TOTAL ADEUDADO
                                                                            </td>
                                                                            <td style={{ padding: '16px', textAlign: 'right', fontWeight: '700', fontSize: '16px', color: '#991b1b' }}>
                                                                                ${casasConAdeudo.reduce((sum, c) => sum + (parseFloat(c.adeudo) || 0), 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>

                                                            <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '16px', marginTop: '20px' }}>
                                                                <p style={{ fontSize: '13px', color: '#1e40af', marginBottom: '0', lineHeight: '1.5' }}>
                                                                    <strong>💡 Nota:</strong> Los adeudos mostrados corresponden al saldo pendiente de cada casa según los registros del sistema.
                                                                    Para más detalles, consulta el módulo de Pagos o el estado de cuenta individual de cada vivienda.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </section>
                                        )}

                                        {activeReportTab === 'fondo' && (
                                            <section className="reporte-section">
                                                <div style={{
                                                    maxWidth: '900px',
                                                    margin: '0 auto',
                                                    padding: '20px'
                                                }}>
                                                    <h3 style={{
                                                        fontSize: '20px',
                                                        fontWeight: '600',
                                                        marginBottom: '24px',
                                                        color: '#1f2937',
                                                        textAlign: 'center'
                                                    }}>
                                                        Gestión de Fondo Mensual
                                                    </h3>

                                                    <div style={{
                                                        backgroundColor: '#f8fafc',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        padding: '24px',
                                                        marginBottom: '24px'
                                                    }}>
                                                        <label style={{
                                                            display: 'block',
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: '#475569',
                                                            marginBottom: '8px'
                                                        }}>
                                                            Saldo Inicial de Fondo
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={saldoInicial}
                                                            onChange={(e) => setSaldoInicial(e.target.value)}
                                                            placeholder="0.00"
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                fontSize: '16px',
                                                                border: '1px solid #cbd5e1',
                                                                borderRadius: '6px',
                                                                outline: 'none',
                                                                fontFamily: 'inherit',
                                                                backgroundColor: 'white'
                                                            }}
                                                        />
                                                        <p style={{
                                                            fontSize: '12px',
                                                            color: '#64748b',
                                                            marginTop: '8px',
                                                            marginBottom: '0',
                                                            fontStyle: reporteGuardado?.contenidoHtml ? 'italic' : 'normal'
                                                        }}>
                                                            {reporteGuardado?.contenidoHtml
                                                                ? '⚠️ Este reporte ya fue guardado. Puedes actualizar el saldo inicial desde aquí y se actualizará en el reporte guardado al presionar "Guardar Saldo Final de Fondo".'
                                                                : 'Este es el saldo de fondo del mes anterior o el inicial del fraccionamiento'}
                                                        </p>
                                                    </div>

                                                    <div style={{
                                                        backgroundColor: '#ffffff',
                                                        border: '2px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        padding: '24px',
                                                        marginBottom: '24px'
                                                    }}>
                                                        <h4 style={{
                                                            fontSize: '16px',
                                                            fontWeight: '600',
                                                            marginBottom: '16px',
                                                            color: '#1f2937'
                                                        }}>
                                                            Cálculo del Saldo Final
                                                        </h4>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <tbody>
                                                                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                                                    <td style={{ padding: '12px 0', fontSize: '16px', fontWeight: '500', textAlign: 'left' }}>Saldo Inicial de Fondo</td>
                                                                    <td style={{ padding: '12px 0', fontSize: '16px', fontWeight: 'bold', textAlign: 'right', minWidth: '140px', color: '#3b82f6' }}>
                                                                        {formatCurrencyReport(Number(saldoInicial) || 0)}
                                                                    </td>
                                                                </tr>
                                                                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                                                    <td style={{ padding: '12px 0', fontSize: '16px', fontWeight: '500', textAlign: 'left' }}>+ Total Ingresos</td>
                                                                    <td style={{ padding: '12px 0', fontSize: '16px', fontWeight: 'bold', textAlign: 'right', minWidth: '140px', color: '#16a34a' }}>
                                                                        {formatCurrencyReport(totalIngresos)}
                                                                    </td>
                                                                </tr>
                                                                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                                                    <td style={{ padding: '12px 0', fontSize: '16px', fontWeight: '500', textAlign: 'left' }}>- Total Egresos</td>
                                                                    <td style={{ padding: '12px 0', fontSize: '16px', fontWeight: 'bold', textAlign: 'right', minWidth: '140px', color: '#dc2626' }}>
                                                                        {formatCurrencyReport(totalEgresos)}
                                                                    </td>
                                                                </tr>
                                                                <tr style={{
                                                                    backgroundColor: saldoFinal >= 0 ? '#f0fdf4' : '#fef2f2',
                                                                    borderTop: '2px solid #94a3b8'
                                                                }}>
                                                                    <td style={{
                                                                        padding: '16px 0',
                                                                        fontSize: '18px',
                                                                        fontWeight: '700',
                                                                        textAlign: 'left'
                                                                    }}>
                                                                        = Saldo Final de Fondo
                                                                    </td>
                                                                    <td style={{
                                                                        padding: '16px 0',
                                                                        fontSize: '20px',
                                                                        fontWeight: '700',
                                                                        textAlign: 'right',
                                                                        minWidth: '140px',
                                                                        color: saldoFinalColor
                                                                    }}>
                                                                        {saldoFinal >= 0 ? formatCurrencyReport(saldoFinal) : `- ${formatCurrencyReport(Math.abs(saldoFinal))}`}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    <div style={{
                                                        backgroundColor: '#fffbeb',
                                                        border: '1px solid #fcd34d',
                                                        borderRadius: '8px',
                                                        padding: '16px',
                                                        marginBottom: '24px',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '12px'
                                                    }}>
                                                        <FaExclamationTriangle style={{ color: '#f59e0b', fontSize: '20px', marginTop: '2px', flexShrink: 0 }} />
                                                        <div>
                                                            <p style={{
                                                                fontSize: '14px',
                                                                fontWeight: '600',
                                                                color: '#92400e',
                                                                marginBottom: '4px'
                                                            }}>
                                                                Importante
                                                            </p>
                                                            <p style={{
                                                                fontSize: '13px',
                                                                color: '#78350f',
                                                                marginBottom: '0',
                                                                lineHeight: '1.5'
                                                            }}>
                                                                Al guardar el saldo final de fondo, este valor se convertirá en el <strong>saldo inicial del siguiente mes</strong>.
                                                                Este proceso permite llevar un registro continuo del fondo del fraccionamiento.
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => handleGuardarSaldoInicial(true)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '14px 24px',
                                                            backgroundColor: '#3b82f6',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            fontSize: '16px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '8px'
                                                        }}
                                                        onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                                                        onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                                    >
                                                        <FaCheckCircle />
                                                        Guardar Saldo Final de Fondo
                                                    </button>
                                                </div>
                                            </section>
                                        )}
                                    </>
                                );
                            })()}

                        </div>

                        <div className="reporte-modal-footer">
                            <button className="btn-close-reporte" onClick={closeReport}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Zen para seleccionar mes/año de Reporte Detallado */}
            {isReportOpen && !reporteDetallado && (
                <div className="reporte-modal-overlay" onClick={closeReport}>
                    <div className="reporte-modal-content reporte-modal-config" onClick={(e) => e.stopPropagation()}>
                        {/* Cabecera Zen */}
                        <div className="reporte-config-header">
                            <div className="reporte-config-icon-badge">
                                <FaChartBar />
                            </div>
                            <div className="reporte-config-header-text">
                                <h2>Generar Reporte Detallado</h2>
                                <p>Consolida ingresos, egresos por bolsas y balance de tesorería</p>
                            </div>
                            <button className="reporte-close-btn" onClick={closeReport} title="Cerrar">
                                <FaTimes />
                            </button>
                        </div>

                        <div className="reporte-modal-body reporte-config-body">
                            {/* Período Activo Badge */}
                            <div className="reporte-periodo-badge-row">
                                <span className="reporte-periodo-badge">
                                    <FaCalendarAlt style={{ marginRight: '6px' }} />
                                    Período Activo: <strong>{getMonthLabel(selectedMonthReport)}</strong>
                                </span>
                            </div>

                            {/* Atajos Rápidos */}
                            <div className="reporte-shortcuts-section">
                                <span className="reporte-shortcuts-label">Atajos Rápidos:</span>
                                <div className="reporte-shortcuts-pills">
                                    <button
                                        type="button"
                                        className={`reporte-shortcut-pill ${esAtajoActivo('actual') ? 'active' : ''}`}
                                        onClick={() => aplicarAtajoPeriodoReporte('actual')}
                                    >
                                        <FaBolt size={10} style={{ marginRight: '4px' }} /> Mes Actual
                                    </button>
                                    <button
                                        type="button"
                                        className={`reporte-shortcut-pill ${esAtajoActivo('anterior') ? 'active' : ''}`}
                                        onClick={() => aplicarAtajoPeriodoReporte('anterior')}
                                    >
                                        <FaHistory size={10} style={{ marginRight: '4px' }} /> Mes Anterior
                                    </button>
                                    <button
                                        type="button"
                                        className={`reporte-shortcut-pill ${esAtajoActivo('haceDos') ? 'active' : ''}`}
                                        onClick={() => aplicarAtajoPeriodoReporte('haceDos')}
                                    >
                                        <FaCalendarCheck size={10} style={{ marginRight: '4px' }} /> Hace 2 Meses
                                    </button>
                                </div>
                            </div>

                            {/* Selectores de Mes y Año en Grid Dual */}
                            <div className="reporte-selector-grid">
                                <div className="reporte-select-card">
                                    <label>
                                        <FaCalendarAlt className="select-icon" /> Mes del Reporte
                                    </label>
                                    <div className="select-wrapper">
                                        <select
                                            value={selectedMonthReport}
                                            onChange={(e) => setSelectedMonthReport(Number(e.target.value))}
                                            className="reporte-select-input"
                                        >
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                                                const mesTxt = new Date(2024, m - 1).toLocaleDateString('es-MX', { month: 'long' });
                                                return (
                                                    <option key={m} value={m}>
                                                        {mesTxt.charAt(0).toUpperCase() + mesTxt.slice(1)}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </div>

                                <div className="reporte-select-card">
                                    <label>
                                        <FaCalendarCheck className="select-icon" /> Año del Reporte
                                    </label>
                                    <div className="select-wrapper">
                                        <select
                                            value={selectedYearReport}
                                            onChange={(e) => setSelectedYearReport(Number(e.target.value))}
                                            className="reporte-select-input"
                                        >
                                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                                                <option key={y} value={y}>
                                                    {y}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Tarjeta de Alcance y Contenido Incluido */}
                            <div className="reporte-scope-card">
                                <div className="reporte-scope-header">
                                    <FaFileAlt className="scope-icon" />
                                    <span>Contenido Incluido en el Reporte Oficial</span>
                                </div>
                                <div className="reporte-scope-items">
                                    <div className="scope-item">
                                        <span className="scope-dot income">•</span>
                                        <span><strong>Ingresos:</strong> Cuotas de mantenimiento, extraordinarias, adeudos y sanciones.</span>
                                    </div>
                                    <div className="scope-item">
                                        <span className="scope-dot expense">•</span>
                                        <span><strong>Egresos:</strong> Gastos fijos, variables y su amortización por bolsas.</span>
                                    </div>
                                    <div className="scope-item">
                                        <span className="scope-dot balance">•</span>
                                        <span><strong>Conciliación:</strong> Saldo inicial, flujo neto, saldo final y convenios vigentes.</span>
                                    </div>
                                </div>

                                {/* Micro-métrica previa de movimientos detectados */}
                                {(() => {
                                    const movs = contarMovimientosMes(selectedMonthReport, selectedYearReport);
                                    return (
                                        <div className="reporte-preflight-info">
                                            {movs.total > 0 ? (
                                                <span className="preflight-status success">
                                                    <FaCheckCircle style={{ marginRight: '5px' }} />
                                                    {movs.total} movimientos registrados detectados ({movs.countIngresos} ingresos, {movs.countEgresos} egresos)
                                                </span>
                                            ) : (
                                                <span className="preflight-status empty">
                                                    <FaExclamationTriangle style={{ marginRight: '5px' }} />
                                                    Sin movimientos registrados en este período
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>

                            {reporteError && <div className="reporte-error">{reporteError}</div>}
                        </div>

                        <div className="reporte-config-footer">
                            <button className="btn-zen-cancel" onClick={closeReport}>Cancelar</button>
                            <button
                                className="btn-zen-generate"
                                onClick={generateDetailedReport}
                                disabled={reporteLoading}
                            >
                                {reporteLoading ? (
                                    <>
                                        <FaSpinner className="spinner" style={{ marginRight: '6px' }} /> Generando Reporte...
                                    </>
                                ) : (
                                    <>
                                        <FaChartBar style={{ marginRight: '6px' }} /> Generar Reporte Detallado
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de error personalizado */}
            {showErrorModal && (
                <div className="error-modal-overlay" onClick={() => setShowErrorModal(false)}>
                    <div className="error-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="error-modal-header">
                            <div className="error-icon-wrapper">
                                <span className="error-icon">✕</span>
                            </div>
                            <h2>Error al calcular cuotas</h2>
                        </div>

                        <div className="error-modal-body">
                            <p className="error-message">{errorMessage}</p>
                        </div>

                        <div className="error-modal-footer">
                            <button
                                className="btn-error-accept"
                                onClick={() => setShowErrorModal(false)}
                            >
                                Aceptar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación personalizado */}
            {showConfirmModal && (
                <div className="confirm-modal-overlay" onClick={() => setShowConfirmModal(false)}>
                    <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-modal-header">
                            <FaCalculator className="confirm-modal-icon" />
                            <h2>
                                {confirmAction === 'marcar_historial'
                                    ? '¿Deseas marcar el historial de pagos ahora?'
                                    : (confirmAction === 'sanciones'
                                        ? '¿Deseas aplicar sanciones (intereses) ahora?'
                                        : '¿Deseas aplicar los cargos mensuales ahora?')}
                            </h2>
                        </div>

                        <div className="confirm-modal-body">
                            <div className="confirm-modal-info">
                                {confirmAction === 'marcar_historial' ? (
                                    <>
                                        <div className="confirm-info-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                                            <span className="confirm-label" style={{ fontWeight: '600' }}>Seleccionar Periodo (Mes y Año):</span>
                                            <div style={{ display: 'flex', gap: '10px', width: '100%', marginTop: '4px' }}>
                                                <select
                                                    value={selectedMonthHistorial}
                                                    onChange={(e) => setSelectedMonthHistorial(Number(e.target.value))}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px 12px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #d1d5db',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        backgroundColor: '#f9fafb'
                                                    }}
                                                >
                                                    {[
                                                        { m: 1, name: 'Enero' },
                                                        { m: 2, name: 'Febrero' },
                                                        { m: 3, name: 'Marzo' },
                                                        { m: 4, name: 'Abril' },
                                                        { m: 5, name: 'Mayo' },
                                                        { m: 6, name: 'Junio' },
                                                        { m: 7, name: 'Julio' },
                                                        { m: 8, name: 'Agosto' },
                                                        { m: 9, name: 'Septiembre' },
                                                        { m: 10, name: 'Octubre' },
                                                        { m: 11, name: 'Noviembre' },
                                                        { m: 12, name: 'Diciembre' }
                                                    ].map(item => (
                                                        <option key={item.m} value={item.m}>{item.name}</option>
                                                    ))}
                                                </select>
                                                <select
                                                    value={selectedYearHistorial}
                                                    onChange={(e) => setSelectedYearHistorial(Number(e.target.value))}
                                                    style={{
                                                        width: '100px',
                                                        padding: '8px 12px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #d1d5db',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        backgroundColor: '#f9fafb'
                                                    }}
                                                >
                                                    {[2024, 2025, 2026, 2027, 2028].map(y => (
                                                        <option key={y} value={y}>{y}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="confirm-info-item" style={{ marginTop: '8px' }}>
                                            <span className="confirm-label">Efecto en saldos:</span>
                                            <span className="confirm-value" style={{ color: '#059669', fontWeight: 'bold' }}>Sin modificación de saldos ($0.00 en al corriente)</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {confirmAction === 'cuotas' && (
                                            <div className="confirm-info-item">
                                                <span className="confirm-label">Cuota mensual:</span>
                                                <span className="confirm-value">{formatCurrency(configQuick.montoMensual)}</span>
                                            </div>
                                        )}
                                        <div className="confirm-info-item">
                                            <span className="confirm-label">
                                                {confirmAction === 'sanciones' ? 'Día de inicio de sanciones:' : 'Día de corte configurado:'}
                                            </span>
                                            <span className="confirm-value">
                                                {confirmAction === 'sanciones'
                                                    ? (configQuick.sancionesInicioDia || 'No definido')
                                                    : (configQuick.corteDia || 'No definido')}
                                            </span>
                                        </div>
                                        {configQuick.interesesEnabled && (
                                            <div className="confirm-info-item">
                                                <span className="confirm-label">Intereses activos:</span>
                                                <span className="confirm-value">{interestTypeLabel}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <p className="confirm-modal-warning">
                                {confirmAction === 'marcar_historial'
                                    ? 'Esto registrará la bandera de saldo actual en el historial de transacciones de todos los residentes sin aplicar nuevos cobros.'
                                    : (confirmAction === 'sanciones'
                                        ? 'Esto aplicará intereses moratorios sobre los adeudos vencidos.'
                                        : 'Esto generará cargos para todas las casas del fraccionamiento.')}
                            </p>
                        </div>

                        <div className="confirm-modal-footer">
                            <button
                                className="btn-confirm-cancel"
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn-confirm-accept"
                                onClick={
                                    confirmAction === 'marcar_historial'
                                        ? handleConfirmMarcarHistorialPagos
                                        : (confirmAction === 'sanciones' ? handleConfirmAplicarSanciones : handleConfirmCalcular)
                                }
                            >
                                Aceptar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de éxito personalizado con resumen de domicilios */}
            {showSuccessModal && successDetails && (
                <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
                    <div className="success-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '580px', width: '92%' }}>
                        <div className="success-modal-header">
                            <div className="success-icon-wrapper">
                                <span className="success-icon">✓</span>
                            </div>
                            <h2>
                                {successDetails.action === 'marcar_historial'
                                    ? 'Historial de Pagos Marcado'
                                    : (successDetails.action === 'sanciones'
                                        ? 'Sanciones Aplicadas'
                                        : 'Cargos Mensuales Aplicados')}
                            </h2>
                        </div>

                        <div className="success-modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            <p className="success-message" style={{ margin: '0 0 12px 0', fontSize: '0.95rem' }}>{successDetails.message}</p>
                            <div className="success-info" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: '8px' }}>
                                <div className="success-info-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span className="success-label" style={{ fontWeight: '600', color: '#166534' }}>Total domicilios procesados:</span>
                                    <span className="success-value" style={{ fontWeight: '800', fontSize: '1.15rem', color: '#15803d' }}>{successDetails.casasActualizadas}</span>
                                </div>
                            </div>

                            {/* Resumen detallado de domicilios procesados */}
                            {successDetails.detallesCasas && successDetails.detallesCasas.length > 0 && (
                                <div className="success-details-list" style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#374151', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>📋 Resumen de domicilios y saldos resultantes:</span>
                                    </h4>
                                    <div style={{ maxHeight: '220px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                                        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 }}>
                                                    <th style={{ padding: '8px 12px', color: '#4b5563' }}>Domicilio</th>
                                                    <th style={{ padding: '8px 12px', color: '#4b5563', textAlign: 'center' }}>Estado</th>
                                                    <th style={{ padding: '8px 12px', color: '#4b5563', textAlign: 'right' }}>Saldo / Adeudo</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {successDetails.detallesCasas.map((det, idx) => {
                                                    const adeudoVal = det.nuevoAdeudo !== undefined ? Number(det.nuevoAdeudo) : (det.adeudo !== undefined ? Number(det.adeudo) : 0);
                                                    const saldoFavorVal = det.nuevoSaldoFavor !== undefined ? Number(det.nuevoSaldoFavor) : (det.saldoFavor !== undefined ? Number(det.saldoFavor) : 0);

                                                    const tieneAdeudo = adeudoVal > 0;
                                                    const tieneSaldoFavor = saldoFavorVal > 0;

                                                    let estadoLabel = '✅ AL CORRIENTE';
                                                    let estadoStyle = {
                                                        backgroundColor: '#dcfce7',
                                                        color: '#15803d',
                                                        border: '1px solid #86efac'
                                                    };
                                                    let montoText = formatCurrency(0);
                                                    let montoStyle = { color: '#15803d' };

                                                    if (tieneAdeudo) {
                                                        estadoLabel = '🚨 CON ADEUDO';
                                                        estadoStyle = {
                                                            backgroundColor: '#fee2e2',
                                                            color: '#991b1b',
                                                            border: '1px solid #fca5a5'
                                                        };
                                                        montoText = `-${formatCurrency(adeudoVal)}`;
                                                        montoStyle = { color: '#b91c1c' };
                                                    } else if (tieneSaldoFavor) {
                                                        estadoLabel = '✅ SALDO A FAVOR';
                                                        estadoStyle = {
                                                            backgroundColor: '#eff6ff',
                                                            color: '#1e40af',
                                                            border: '1px solid #bfdbfe'
                                                        };
                                                        montoText = `+${formatCurrency(saldoFavorVal)}`;
                                                        montoStyle = { color: '#1d4ed8' };
                                                    }

                                                    const casaNum = det.numero || det.casaNumero || det.casaId || (idx + 1);

                                                    return (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                                                            <td style={{ padding: '8px 12px', fontWeight: '600', color: '#1f2937' }}>
                                                                Vivienda {casaNum}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                <span style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    padding: '3px 10px',
                                                                    borderRadius: '16px',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: '700',
                                                                    ...estadoStyle
                                                                }}>
                                                                    {estadoLabel}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', ...montoStyle, whiteSpace: 'nowrap' }}>
                                                                {montoText}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="success-modal-footer">
                            <button
                                className="btn-success-accept"
                                onClick={() => setShowSuccessModal(false)}
                            >
                                Aceptar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación personalizado para cerrar */}
            {confirmCloseModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '32px',
                        maxWidth: '450px',
                        width: '90%',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '48px',
                            marginBottom: '16px'
                        }}>
                            ⚠️
                        </div>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#1f2937',
                            marginBottom: '12px'
                        }}>
                            Tienes información sin guardar
                        </h2>
                        <p style={{
                            fontSize: '15px',
                            color: '#6b7280',
                            marginBottom: '24px',
                            lineHeight: '1.6'
                        }}>
                            Los datos capturados en sanciones {multasManual.length > 0 ? '(' + multasManual.length + ' sanciones)' : ''} {notasReporte ? 'y notas' : ''} se perderán si cierras este modal.
                            <br />
                            <br />
                            <strong>¿Estás seguro de que deseas continuar?</strong>
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={() => setConfirmCloseModal(false)}
                                style={{
                                    padding: '10px 28px',
                                    fontSize: '15px',
                                    fontWeight: '500',
                                    border: '2px solid #e5e7eb',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '120px'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f3f4f6';
                                    e.target.style.borderColor = '#d1d5db';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'white';
                                    e.target.style.borderColor = '#e5e7eb';
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={realizarCierre}
                                style={{
                                    padding: '10px 28px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    border: 'none',
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '120px'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#dc2626';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#ef4444';
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal / Drawer de Desglose de Bolsa de Centavos y Tesorería */}
            {isBolsaCentavosModalOpen && (
                <div className="reporte-modal-overlay" onClick={() => setIsBolsaCentavosModalOpen(false)}>
                    <div
                        className="reporte-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: '720px', width: '92%', borderRadius: '16px', overflow: 'hidden' }}
                    >
                        <div className="reporte-modal-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '16px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ background: '#fef3c7', padding: '8px', borderRadius: '10px', display: 'flex' }}>
                                    <FaCoins style={{ color: '#d97706', fontSize: '1.25rem' }} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b', fontWeight: 700 }}>
                                        Bolsa de Centavos y Tesorería
                                    </h3>
                                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                        Ingresos misceláneos y redondeos no asignados a cuotas
                                    </span>
                                </div>
                            </div>
                            <button className="reporte-close-btn" onClick={() => setIsBolsaCentavosModalOpen(false)}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="reporte-modal-body" style={{ padding: '20px' }}>
                            {/* Tarjeta de Resumen */}
                            <div style={{
                                background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                                border: '1px solid #ddd6fe',
                                borderRadius: '12px',
                                padding: '16px 20px',
                                marginBottom: '20px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '14px'
                            }}>
                                <div>
                                    <div style={{ fontSize: '0.8rem', color: '#6d28d9', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Acumulado del Mes
                                    </div>
                                    <div style={{ fontSize: '1.8rem', color: '#5b21b6', fontWeight: 800, marginTop: '2px' }}>
                                        {formatCurrency(summary.bolsaCentavos || 0)}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#7c3aed', marginTop: '4px' }}>
                                        {(summary.partidasCentavos || []).length} {(summary.partidasCentavos || []).length === 1 ? 'partida registrada' : 'partidas registradas'}
                                    </div>
                                </div>
                                <div style={{ maxWidth: '320px', fontSize: '0.75rem', color: '#4c1d95', background: 'rgba(255,255,255,0.7)', padding: '10px 12px', borderRadius: '8px', lineHeight: '1.4' }}>
                                    ℹ️ <strong>Transparencia Contable:</strong> Estos centavos o ingresos varios pasan directamente a la cuenta de tesorería del fraccionamiento. No amortizan adeudos ni generan saldo a favor para el residente.
                                </div>
                            </div>

                            {/* Listado de Partidas */}
                            <h4 style={{ fontSize: '0.9rem', color: '#334155', fontWeight: 700, marginBottom: '10px' }}>
                                Desglose de Movimientos del Período
                            </h4>

                            {(!summary.partidasCentavos || summary.partidasCentavos.length === 0) ? (
                                <div style={{ textAlign: 'center', padding: '30px 10px', color: '#94a3b8', fontSize: '0.9rem' }}>
                                    No hay registros de centavos o ingresos misceláneos en este período.
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                    <table className="reporte-table" style={{ margin: 0, width: '100%', fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr style={{ background: '#f8fafc' }}>
                                                <th style={{ padding: '8px 12px' }}>Casa</th>
                                                <th style={{ padding: '8px 12px' }}>Folio</th>
                                                <th style={{ padding: '8px 12px' }}>Concepto</th>
                                                <th style={{ padding: '8px 12px' }}>Método / Ref</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Monto</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.partidasCentavos.map((item, idx) => (
                                                <tr key={idx} style={{ borderBottom: idx < summary.partidasCentavos.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e293b' }}>
                                                        Casa {item.casaNumero}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', color: '#2563eb', fontWeight: 600, fontSize: '0.8rem' }}>
                                                        {item.folio}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', color: '#475569' }}>
                                                        {item.concepto}
                                                        {item.detalle ? <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: '4px' }}>({item.detalle})</span> : null}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', color: '#64748b', fontSize: '0.8rem' }}>
                                                        {item.metodoPago}{item.referencia && item.referencia !== 'N/A' ? ` · #${item.referencia}` : ''}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>
                                                        {formatCurrency(item.monto)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => setIsBolsaCentavosModalOpen(false)}
                                    style={{
                                        padding: '8px 18px',
                                        backgroundColor: '#64748b',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '0.88rem',
                                        fontWeight: 600
                                    }}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ReportesModal
                isOpen={showReporteMensualModal}
                onClose={() => setShowReporteMensualModal(false)}
                fraccionamientoId={fraccionamientoId}
                reportType="mensual"
            />

            {/* Modal para Saldo Inicial */}
            <ConfirmationModal
                open={saldoModal.open}
                title={saldoModal.title}
                message={saldoModal.message}
                type={saldoModal.type}
                icon={saldoModal.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                actionLabel="Aceptar"
                onClose={() => setSaldoModal({ ...saldoModal, open: false })}
            />
        </div>
    );
};

export default Finanzas;
